/**
 * CreateDispatchModal — book a service visit as a FLOW (SCHED-FLOW-01, Seth
 * 2026-09-17: "make it larger and more of a flow through the steps from
 * beginning to end … I want scheduling to be super fluid, easy, but capture
 * everything we need and scheduling it to the board simple"):
 *   1. WHAT — type-to-search the service request; Pegasus fills the notes,
 *      urgency and diagnostic fee; PO / job number.
 *   2. WHEN & WHO — the availability grid: two weeks of days across, arrival
 *      windows down, every cell the best-fitting tech scored by the copilot's
 *      board math. Tech chips; the customer's availability (SCHED-AVAIL-01,
 *      Seth 2026-09-17: "nothing should be preselected … save with date …
 *      no ways of picking days of the week"): Days chips (Any day · Mon–Sat),
 *      cross a day OR a window off on its header, a "customer said" note —
 *      all blank until the customer says something; what's on file shows as
 *      a hint with a Use button, never applied on its own; what the
 *      dispatcher captures is saved to the customer's record WITH THE DATE
 *      (a window_pref watch-out) when the visit is booked or queued. The
 *      urgency (ASAP / Soon / Routine) is switchable on this step and drives
 *      the grid's reading: earliest first / best fit / lightest days marked.
 *      Click a cell to pick / another to swap; the chosen day's run order
 *      underneath sets an exact time by drag. "By hand" keeps the tech / day
 *      / window inputs for the odd case.
 *   3. CONFIRM — heads-up on the way, files & photos, the summary → board.
 * A summary rail fills in as you go; Save to queue is one click on any step.
 * Everything the June 23 one-screen version captured is still captured. The
 * date range is gone — paging the grid by week is the same gesture. ESC is
 * never touched (DEC-22/25).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarPlus, Check, CalendarClock, Search, Sparkles, Phone, Inbox, X, Star, GraduationCap, Ban, AlertTriangle, Paperclip, Plus, FileText, ArrowRight, ArrowLeft, Clock, Wrench, ShieldCheck, BadgeDollarSign, Hammer, RotateCcw, Droplets } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { useCreatePegasusDispatch } from "./dispatchCreate";
import { useScheduleDispatch } from "./scheduleDispatch";
import { useCustomerSignals, useCustomerStatusFlag, isDoNotService, useAddSignal, useResolveSignal, type CustomerSignal } from "./api";
import { setTechRequest } from "./techRequest";
import { onErrToast } from "@/components/ui/mutationToast";
import { useOncallDaysPublished } from "@/features/team/oncall";
import { useLevelLabels, levelNeedText, useSpecialties, useTechQuals, missReasons, systemsForType, equipmentNeeds, useCallEquipment } from "@/features/team/skillSteer";
import { useEquipmentCategories } from "@/features/settings/equipmentCategories";
import { showOnBoard } from "@/lib/boardChannel";
import { useListNav, scrollActiveIntoView } from "@/lib/keys";
import { TechDayList } from "./TechDayList";
import { windowColors } from "@/lib/window";
import { STATUS_DONE } from "@/lib/types";
import { useMembershipStatus } from "@/features/agreements/api";
import { useCustomerDispatches } from "./api";
import { WhoToReach, useReachPeople, reachPeople, defaultConfirmKey, resolveReach, reachSummary, persistReach, prettyPhone, EMPTY_REACH, type ReachState } from "./WhoToReach";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useActiveTechs } from "@/features/board/api";
import { availabilityGrid, cellKey, DEFAULT_ROUTINE_CAP, type TimeSuggestion, type GridResult, type LaneStop } from "@/features/board/suggestTimes";
import { AvailabilityGrid } from "./AvailabilityGrid";
import { useOrgSettingNumber } from "@/lib/orgSettings";
import { useUpdateTicket, type PendingTicket } from "@/features/board/queue";
import { uploadFilesTo, prettySize, useOpenRequestMedia, attachRequestMediaTo, fileWhen } from "@/features/files/filesCore";
import {
  useServiceTypes,
  useResolveFee,
  useFeeContext,
  categoryLabel,
  type ServiceType,
  type Urgency,
} from "./serviceTypes";
import { useCustomerAgreementRenewal } from "@/features/agreements/api";
import { useRecentCalls } from "@/features/calls/calls";
import type { BuilderUrgency } from "@/lib/intakeMap";
import { INSTALL_TIME_WINDOWS as INSTALL_WINDOWS, INSTALL_ONLY_WINDOWS, INSTALL_WINDOW_LABELS } from "@/lib/format";

// Bookable arrival windows — the SAME codes the board sorts by, so a booked job
// lands in the right block. "Anytime" = no specific window.
const WINDOWS = ["8AM", "8-12", "10-2", "12-4", "3-7", "AFTER", "Anytime"];
// Installs get their OWN arrival windows + a fillable Custom (Seth 2026-06-30).
// The canonical list lives in lib/format (INSTALL_TIME_WINDOWS), imported above
// as INSTALL_WINDOWS so the board's quick-edit shares the exact same set.
const URGENCY: { key: Urgency; label: string; hint: string; cls: string }[] = [
  { key: "asap", label: "ASAP", hint: "Earliest opening", cls: "border-red-300 bg-red-50 text-red-700" },
  { key: "soon", label: "Soon", hint: "Best fit, next ~2 weeks", cls: "border-amber-300 bg-amber-50 text-amber-700" },
  { key: "routine", label: "Routine", hint: "Fills a light day", cls: "border-slate-300 bg-slate-100 text-slate-700" },
];
/** SCHED-FLOW-01: the grid's day columns — ten working days from a start
 *  (Saturdays only when the customer can do them), paged by whole weeks. */
const isoOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
function buildGridDays(todayISO: string, weekOffset: number, includeSat: boolean): string[] {
  const start = new Date(`${todayISO}T00:00:00`);
  start.setDate(start.getDate() + weekOffset * 7);
  const out: string[] = [];
  const cursor = new Date(start);
  let guard = 0;
  while (out.length < (includeSat ? 12 : 10) && guard < 40) {
    const dow = cursor.getDay();
    if (dow !== 0 && (dow !== 6 || includeSat)) out.push(isoOf(cursor));
    cursor.setDate(cursor.getDate() + 1);
    guard++;
  }
  return out;
}
const SERVICE_WINDOWS = ["8AM", "8-12", "10-2", "12-4", "3-7"];
/** ESC's non-tech columns (Parts, Unassigned, Serv Agr placeholders) — never a "last visit". */
const PSEUDO_TECHS = new Set(["0000", "0001", "7777", "8888", "9999"]);
const WINDOW_LABEL: Record<string, string> = { "8AM": "8 AM first", "8-12": "8–12", "10-2": "10–2", "12-4": "12–4", "3-7": "3–7", AFTER: "After Hours" };

// ── SCHED-AVAIL-01: the customer's availability, as the dispatcher captures it ──
const DOW_LABEL: Record<number, string> = { 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri", 6: "Sat" };
const DOW_LONG: Record<number, string> = { 1: "Mondays", 2: "Tuesdays", 3: "Wednesdays", 4: "Thursdays", 5: "Fridays", 6: "Saturdays" };
const WEEKDAYS = [1, 2, 3, 4, 5];
const dowOf = (iso: string) => new Date(`${iso}T00:00:00`).getDay();
/** What the customer said about when we can come — everything optional, nothing pre-set. */
interface Availability {
  /** Weekdays the customer can do (1 = Mon … 6 = Sat). Mon–Fri = no restriction. */
  days: Set<number>;
  /** Arrival windows the customer can't do. */
  windowsOff: Set<string>;
  /** In the dispatcher's words: "works from home Tue/Thu, needs 30 min notice". */
  note: string;
}
const DEFAULT_DAYS = () => new Set(WEEKDAYS);
const isRestrictedDays = (d: Set<number>) => !WEEKDAYS.every((x) => d.has(x));
/** The record line, in the words the office uses: "Tue & Thu only · can't do 8 AM first or 3–7 · works from home". */
function availabilityText(a: Availability, windowLabel: (w: string) => string): string {
  const parts: string[] = [];
  if (isRestrictedDays(a.days)) {
    const names = [1, 2, 3, 4, 5, 6].filter((d) => a.days.has(d)).map((d) => DOW_LABEL[d]);
    parts.push(names.length === 1 ? `${DOW_LONG[[1, 2, 3, 4, 5, 6].find((d) => a.days.has(d))!]} only` : `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]} only`);
  } else if (a.days.has(6)) parts.push("Saturdays OK");
  if (a.windowsOff.size > 0) {
    const w = [...a.windowsOff].map(windowLabel);
    parts.push(`can't do ${w.length === 1 ? w[0] : `${w.slice(0, -1).join(", ")} or ${w[w.length - 1]}`}`);
  }
  if (a.note.trim()) parts.push(a.note.trim());
  return parts.join(" · ");
}
/** Read a stored watch-out (ours, structured — or the old free-text kind) back into the capture. */
function availabilityFromSignal(sig: CustomerSignal): Partial<Availability> | null {
  const d = (sig.detail ?? {}) as { days?: number[]; windowsOff?: string[]; note?: string };
  if (Array.isArray(d.days) || Array.isArray(d.windowsOff)) {
    const out: Partial<Availability> = {};
    const days = new Set((Array.isArray(d.days) ? d.days : []).filter((x) => x >= 1 && x <= 6));
    if (days.size > 0 && (isRestrictedDays(days) || days.has(6))) out.days = days;
    if (Array.isArray(d.windowsOff) && d.windowsOff.length > 0) out.windowsOff = new Set(d.windowsOff);
    if (typeof d.note === "string" && d.note.trim()) out.note = d.note.trim();
    return Object.keys(out).length > 0 ? out : null;
  }
  // Free text from the notes ("mornings only — leaves for work at 1"): the
  // common phrasings map onto windows and weekdays; anything else is just shown.
  const t = `${sig.value} ${sig.source_text ?? ""}`.toLowerCase();
  const off = new Set<string>();
  if (/not before 9|no earlier than 9|after 9\s*(am|a\.m)|nothing before 9/.test(t)) off.add("8AM");
  if (/mornings? only|before noon only|\bam only\b/.test(t)) { off.add("12-4"); off.add("3-7"); }
  if (/afternoons? only|after noon only|\bpm only\b/.test(t)) { off.add("8AM"); off.add("8-12"); off.add("10-2"); }
  if (/after 3\s*(pm|p\.m)?\b(?! ?(days|weeks|months))|not before 3\s*pm/.test(t)) { off.add("8AM"); off.add("8-12"); off.add("10-2"); off.add("12-4"); }
  const dayWords: [RegExp, number][] = [[/\bmon(day)?s?\b/, 1], [/\btue(s|sday)?s?\b/, 2], [/\bwed(nesday)?s?\b/, 3], [/\bthu(r|rs|rsday)?s?\b/, 4], [/\bfri(day)?s?\b/, 5], [/\bsat(urday)?s?\b/, 6]];
  const named = dayWords.filter(([r]) => r.test(t)).map(([, n]) => n);
  let days: Set<number> | undefined;
  if (named.length > 0) {
    // "closed Fridays" / "never on Mondays" = those days are OUT; otherwise
    // "Tuesdays" / "Tue or Thu only" = ONLY those days.
    if (/closed|\bnot\b|never|can'?t|cannot|except|unavailable|\baway\b|\bgone\b|\boff\b/.test(t)) { days = DEFAULT_DAYS(); for (const n of named) days.delete(n); if (days.size === 0) days = undefined; }
    else days = new Set(named);
  }
  if (off.size === 0 && !days) return null;
  const out: Partial<Availability> = {};
  if (off.size > 0) out.windowsOff = off;
  if (days) out.days = days;
  return out;
}
/** Time-of-day / weekday statements on file — ours or the extractor's. */
const TIME_TALK = /not before|no earlier|mornings?|afternoons?|after \d|before \d|\b(mon|tues?|wed|thu(rs?)?|fri|sat)(days?|s)?\b|weekends?|\d\s*(am|pm)\b/i;

// Drag-to-time helpers (SCHED-MINIBOARD slice 2): a dragged decimal hour ⇄ the
// customer-facing window + a stored "HH:MM" exact time.
const winForHour = (h: number): string => (h < 8.5 ? "8AM" : h < 10 ? "8-12" : h < 12 ? "10-2" : h < 15 ? "12-4" : "3-7");
// Where the new visit sits in the tech's day: an exact (dragged) time wins,
// else the hour its promised window stands for, else the copilot's route slot.
// The option cards, the day list and the Ready card all read this one rule.
const WIN_HOUR: Record<string, number> = { "8AM": 8, "8-12": 9, "10-2": 11, "12-4": 13, "3-7": 16 };
function runOrderOf(o: TimeSuggestion, win: string, exactHour: number | null): { pos: number; after: LaneStop | null } {
  const h = exactHour ?? WIN_HOUR[win] ?? o.slotHour;
  const pos = o.stops.filter((st) => st.hour < h).length;
  return { pos, after: pos > 0 ? o.stops[pos - 1] : null };
}
const hourToHHMM = (h: number): string => {
  let hr = Math.floor(h);
  let m = Math.round((h - hr) * 60);
  if (m === 60) { hr += 1; m = 0; }
  return `${String(hr).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};
const hhmmToHour = (t: string): number => { const [h, m] = t.split(":").map(Number); return h + (m || 0) / 60; };


export function CreateDispatchModal({
  custno, locno, customerName, address, city, phone, onClose, existingTicket = null,
  sourceCallId = null, initialDescription = "", sourceCaption = null,
  prefillUrgency = null, prefillWindow = null, prefillTypeName = null, onCreated,
  prefillEmpno = null, prefillDate = null,
}: {
  custno: string | null; locno: string | null; customerName: string;
  address?: string | null; city?: string | null; phone?: string | null; onClose: () => void;
  /** When set, the modal BOOKS this already-queued ticket in place (status
   * pending→scheduled) instead of creating a new dispatch — so a pending ticket
   * opens the SAME schedule window (Seth 2026-06-24). No service-type required. */
  existingTicket?: PendingTicket | null;
  /** RECORDING→DISPATCH: created from a recorded call — stamps source_call_id so
   * the recording attaches to the new dispatch + plays in its detail. */
  sourceCallId?: string | null;
  /** AI-INTAKE-SUMMARY: seed the description (e.g. an AI summary of a chat /
   * web-form request) — editable before save, so it's propose not auto. */
  initialDescription?: string;
  /** Transparency line when the description is an AI summary, e.g.
   * "AI summary of 6 messages from today" — shown at the top so the dispatcher
   * sees exactly what fed it (Seth 2026-06-26). Doesn't touch the saved notes. */
  sourceCaption?: string | null;
  /** AI-INTAKE field-fill (Seth 2026-06-26): seed Urgency + Arrival window from
   * the parsed request so a web form / recording fills the builder, not just the
   * notes. The customer's stated urgency/window wins over service-type defaults. */
  prefillUrgency?: BuilderUrgency | null;
  prefillWindow?: string | null;
  /** AI's best-guess service type NAME (Seth 2026-06-26) — pre-selected as a
   * clearly-marked SUGGESTION the office confirms or changes; it drives the fee
   * + routing, so never silent. */
  prefillTypeName?: string | null;
  /** Fired once when a dispatch is successfully created/scheduled — lets the
   * caller close the loop (e.g. mark the source conversation Done). Seth 2026-06-26.
   * MEMBERSHIPS-02 P3: carries the outcome so a plan visit can be marked booked
   * with the day (mode "queued" = a pending ticket, no day yet). */
  onCreated?: (outcome: { mode: "scheduled" | "queued"; dispatchId: string | null; date: string | null; empno: string | null }) => void;
  /** BOARD-DBLCLICK-NEW (Seth 2026-08-12): double-clicking empty board space
   * already knows the TECH + DAY — seed them here. Fully changeable after. */
  prefillEmpno?: string | null;
  prefillDate?: string | null;
}) {
  const booking = !!existingTicket;
  // DNS-01 (Seth 2026-08-21): a Do Not Service account can't take new
  // bookings. Derived the same way the header derives it — the Pegasus mark
  // OR a BAD-style ESC credit rating.
  const { data: dnsFlag } = useCustomerStatusFlag(custno);
  const { data: dnsCust } = useQuery({
    queryKey: ["dns_credit", custno],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<string | null> => {
      const { data } = await supabase.from("mirror_customer").select("credit_rating").eq("custno", custno as string).maybeSingle();
      return ((data as { credit_rating?: string | null } | null)?.credit_rating ?? null);
    },
  });
  const dnsBlocked = isDoNotService(dnsFlag, dnsCust);
  const create = useCreatePegasusDispatch();
  const schedule = useScheduleDispatch();
  const update = useUpdateTicket();
  const { data: types } = useServiceTypes();
  const { data: techs } = useActiveTechs("03");
  const { data: renewal } = useCustomerAgreementRenewal(custno ?? undefined);
  const isPlus = !!renewal && renewal.days_left >= 0;
  // BOOK-A-CALL-REDESIGN: the window's band carries the customer's facts —
  // the plan's term (the same rule as the 360 badge), the last visit.
  const { data: membership } = useMembershipStatus(custno ?? undefined);
  const planLabel = membership && ["active", "expiring", "ended"].includes(String(membership.status))
    ? (membership.term_years ? `${membership.term_years}-Year Plan` : "Plan")
    : isPlus ? "Member" : null;
  const { data: visitRows } = useCustomerDispatches(custno ?? undefined);
  const lastVisit = useMemo(() => {
    // A real tech, a finished call — ESC's agreement placeholders ride empno
    // 9999 "Serv Agr" as Pending rows and are not visits.
    const today = isoOf(new Date());
    const r = (visitRows ?? []).find((v) => v.appointment_date && v.appointment_date < today && !PSEUDO_TECHS.has(v.tech_empno) && (STATUS_DONE.has(v.status ?? "") || v.peg));
    if (!r) return null;
    const d = new Date(`${r.appointment_date}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const who = r.tech_first ?? r.tech_name?.split(/\s+/)[0] ?? null;
    return who ? `${d} · ${who}` : d;
  }, [visitRows]);

  // intake
  const [typeId, setTypeId] = useState("");
  const [typeQuery, setTypeQuery] = useState("");
  const [comboOpen, setComboOpen] = useState(false);
  // The AI's pre-selected type is a SUGGESTION until the office confirms/changes it.
  const [typeSuggested, setTypeSuggested] = useState(false);
  const [feeEditing, setFeeEditing] = useState(false);
  const [description, setDescription] = useState(initialDescription);
  const [urgency, setUrgency] = useState<Urgency>(prefillUrgency ?? "soon");
  // CALL-TO-DISPATCH-01 (Seth 2026-10-01: "if we create a dispatch from the
  // call I would like those notes to be in the dispatch — not just slapped
  // on the customer record somewhere"). If this customer is on the phone
  // right now (or hung up minutes ago), the call feeds the DISPATCH: the
  // AI's call summary seeds the tech notes (never stomping anything already
  // typed), and a live call flips the label so the dispatcher knows what
  // they type here rides the visit. One-off instructions ("call my sister,
  // she'll be there") belong on the visit; durable property facts keep
  // flowing to Watch For via the notes signals.
  const recentCalls = useRecentCalls().data ?? [];
  const callForThis = useMemo(() => {
    const mine = recentCalls.filter((c) => c.custno === custno);
    return mine.find((c) => c.state === "ringing" || c.state === "answered") ?? mine[0] ?? null;
  }, [recentCalls, custno]);
  const callLive = !!callForThis && (callForThis.state === "ringing" || callForThis.state === "answered");
  const [seededFromCall, setSeededFromCall] = useState(false);
  const callSeedRef = useRef("");
  useEffect(() => {
    if (!callForThis || existingTicket || seededFromCall) return;
    // CALLS-NOTES-01: the dispatcher's own typed notes LEAD; the AI's call
    // summary follows. Either alone still seeds.
    const seed = [
      (callForThis.dispatcher_notes ?? "").trim(),
      (callForThis.ai_summary ?? "").trim(),
    ].filter(Boolean).join("\n");
    if (!seed || description.trim()) return;
    setSeededFromCall(true);
    callSeedRef.current = seed;
    setDescription(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callForThis, existingTicket]);
  const [feeOverride, setFeeOverride] = useState("");
  const [po, setPo] = useState("");
  // schedule — a board double-click arrives with the tech + day already known.
  const [empno, setEmpno] = useState(prefillEmpno ?? "");
  const [date, setDate] = useState(prefillDate ?? "");
  // Exact arrival time "HH:MM" set by dragging the token; null = window-only.
  const [exactTime, setExactTime] = useState<string | null>(null);
  // Default to Anytime so "Suggest a time" optimizes BOTH the window and the
  // routing (Seth 2026-06-23) — a hard default of 8-12 forced every suggestion there.
  const [win, setWin] = useState(prefillWindow ?? "Anytime");
  // Did the dispatcher explicitly choose the promised window? Then suggestions
  // won't overwrite it; otherwise the window FOLLOWS the route slot (Seth 2026-06-24).
  // A customer-stated window (prefillWindow) counts as chosen — don't auto-move it.
  const winTouched = useRef(!!prefillWindow);
  // WHO-TO-REACH (34i87): the confirmation text, the heads-up on the way (call
  // or text — a different number if they like) and an email, each pointed at a
  // line on the record or at someone typed in place. Replaces the old
  // pick-a-number row + one-off contact box (Seth 2026-06-24 / 2026-09-02).
  const qc = useQueryClient();
  const canSaveContacts = useCan("contacts.edit");
  const [reach, setReach] = useState<ReachState>(EMPTY_REACH);
  const { lines: reachLines, ready: reachReady } = useReachPeople(custno, locno);
  const people = useMemo(() => reachPeople(reachLines, reach.other), [reachLines, reach.other]);
  useEffect(() => {
    // The best mobile takes the confirmation text by default. The lines arrive
    // in two steps (the account's phone bank, then the property's grid) and
    // their keys change between them — so a pick that no longer exists is
    // re-defaulted, and a pick that still exists is never overridden.
    if (!reachReady || reachLines.length === 0) return;
    setReach((r) => {
      const has = (k: string | null) => !k || k === "other" || reachLines.some((l) => l.key === k);
      const confirmOk = !!r.confirmKey && has(r.confirmKey);
      const next = confirmOk && has(r.headsUpKey) && has(r.emailKey) ? r : {
        ...r,
        confirmKey: confirmOk ? r.confirmKey : defaultConfirmKey(reachLines, phone),
        headsUpKey: has(r.headsUpKey) ? r.headsUpKey : null,
        emailKey: has(r.emailKey) ? r.emailKey : null,
      };
      return next;
    });
  }, [reachReady, reachLines, phone]);
  const reached = useMemo(() => resolveReach(people, reach), [people, reach]);
  // SCHED-FLOW-01 — the flow + the grid
  const [step, setStep] = useState<1 | 2 | 3>(booking ? 2 : 1);
  // Tech row: the techs the grid is limited to ([] = any tech). Several at
  // once when the customer is fine with more than one ("Wayne or Miles").
  const [techPick, setTechPick] = useState<string[]>([]);
  const techPickTouched = useRef(false);
  // SCHED-AVAIL-01: blank until the customer says something.
  const [avail, setAvail] = useState<Availability>(() => ({ days: DEFAULT_DAYS(), windowsOff: new Set(), note: "" }));
  const [remember, setRemember] = useState(true);
  // The line on file re-applied as-is: not saved twice unless asked (a fresh date).
  const [resave, setResave] = useState(false);
  const [crossed, setCrossed] = useState<Set<string>>(() => new Set());
  const [weekOffset, setWeekOffset] = useState(0);
  const [grid, setGrid] = useState<GridResult | null>(null);
  const [gridLoading, setGridLoading] = useState(false);
  const [selectedOpt, setSelectedOpt] = useState<TimeSuggestion | null>(null);
  const [byHand, setByHand] = useState(false);
  // result
  const [outcome, setOutcome] = useState<null | { mode: "scheduled" | "queued"; dispatchId?: string | null }>(null);
  // Tell the caller a dispatch landed (once) — used to offer "mark the request done".
  useEffect(() => {
    if (outcome) onCreated?.({ mode: outcome.mode, dispatchId: outcome.dispatchId ?? null, date: outcome.mode === "scheduled" ? (date || null) : null, empno: outcome.mode === "scheduled" ? (empno || null) : null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome]);
  // Files staged in the modal — uploaded + pinned to the dispatch on save (no
  // dispatch id exists yet mid-creation). Customer-scoped store, so they also
  // land on the 360 + mobile. (Seth: attach during creation OR after.)
  const [staged, setStaged] = useState<File[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  // FILES-CALL-01: photos the customer sent with a portal request ride onto
  // the call — copied into the customer's files, pinned to the dispatch,
  // labeled "from the customer" so the tech knows they're for THIS visit.
  const { data: requestMedia } = useOpenRequestMedia(custno);
  const [attachRequest, setAttachRequest] = useState(true);
  const requestPhotoCount = (requestMedia ?? []).reduce((n, r) => n + r.media.length, 0);
  const uploadStaged = async (dispatchId: string) => {
    if (!custno) return;
    if (staged.length) {
      try {
        await uploadFilesTo({ custno, locno, dispatch: dispatchId }, staged);
      } catch {
        /* non-fatal — the dispatch is already booked; files can be re-added there */
      }
    }
    if (attachRequest && requestMedia?.length) {
      for (const r of requestMedia) {
        try { await attachRequestMediaTo({ custno, locno, dispatch: dispatchId }, r); } catch { /* best effort */ }
      }
      qc.invalidateQueries({ queryKey: ["portal_request_media"] });
      qc.invalidateQueries({ queryKey: ["portal_requests_new"] });
      qc.invalidateQueries({ queryKey: ["dispatch_files"] });
      qc.invalidateQueries({ queryKey: ["customer_files"] });
    }
  };

  const selected = (types ?? []).find((t) => t.id === typeId) ?? null;
  // An install swaps the arrival-window choices to the install set (Seth 2026-06-30).
  const isInstall = selected?.category === "install";
  // Default an install to its first window; revert an install-only window back to
  // Anytime when it's no longer an install — but never stomp a window the
  // dispatcher (or the intake prefill) already chose.
  useEffect(() => {
    if (winTouched.current) return;
    if (isInstall) {
      if (!INSTALL_WINDOWS.includes(win)) setWin(INSTALL_WINDOWS[0]);
    } else if (INSTALL_ONLY_WINDOWS.includes(win)) {
      setWin("Anytime");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInstall]);
  // Pre-select the AI's suggested type once the catalog loads — as a SUGGESTION
  // (office can change it). Doesn't touch notes/urgency (those keep the AI fill).
  useEffect(() => {
    if (!prefillTypeName || typeId || !(types ?? []).length) return;
    const match = (types ?? []).find(
      (t) => t.name.toLowerCase() === prefillTypeName.toLowerCase(),
    );
    if (match) {
      setTypeId(match.id);
      setTypeQuery(match.name);
      setTypeSuggested(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillTypeName, types]);

  // SVC-PICK-02 (Seth 2026-10-20: "the service request drop down is ugly and
  // messy … I can't go through all the options, I'll have to type, and can't
  // view any that I don't know are there"). The August fix (SEARCH-FIRST,
  // after "a huge ass list") hid the catalog behind typing; the catalog is 28
  // requests in six groups — small enough to SHOW, badly enough shaped to need
  // a shape. So: the whole catalog, grouped, in two balanced columns, every
  // row with its time; typing filters it (name, category, description) and
  // keeps the grouping. ↓ ↑ Enter still walk the rows in reading order.
  const typeSearch = useMemo(() => {
    const q = typeQuery.trim().toLowerCase();
    const hit = (t: ServiceType) =>
      !q || t.name.toLowerCase().includes(q) || categoryLabel(t.category).toLowerCase().includes(q) || (t.default_description ?? "").toLowerCase().includes(q);
    const matched = (types ?? []).filter(hit);
    const m = new Map<string, ServiceType[]>();
    for (const t of matched) m.set(t.category, [...(m.get(t.category) ?? []), t]);
    const groups = [...m.entries()];
    // Two columns, balanced by row count (a group never splits): the biggest
    // group anchors the left column, the rest fill whichever side is shorter.
    const cols: [Array<[string, ServiceType[]]>, Array<[string, ServiceType[]]>] = [[], []];
    const rows = [0, 0];
    for (const g of [...groups].sort((a, b) => b[1].length - a[1].length)) {
      const side = rows[0] <= rows[1] ? 0 : 1;
      cols[side].push(g);
      rows[side] += g[1].length + 1;
    }
    // Reading order for the keyboard: left column top→bottom, then right.
    const ordered = [...cols[0], ...cols[1]];
    return { groups: ordered, cols, total: (types ?? []).length, matched: matched.length };
  }, [types, typeQuery]);
  const groups = typeSearch.groups;
  // KEYS-01 (Seth 2026-09-22): the request list is keyboard-first — type,
  // ↓ ↑ to move, Enter picks; Tab picks the highlighted match and moves on.
  const flatTypes = useMemo(() => groups.flatMap(([, list]) => list), [groups]);
  const typeListRef = useRef<HTMLDivElement>(null);
  const listOpen = comboOpen && !selected;
  const typeNav = useListNav({
    count: flatTypes.length,
    open: listOpen,
    typed: typeQuery.trim().length > 0,
    onPick: (i) => { const t = flatTypes[i]; if (t) onSelectType(t); },
    onClose: () => setComboOpen(false),
  });
  useEffect(() => { if (listOpen) scrollActiveIntoView(typeListRef.current, typeNav.active); }, [listOpen, typeNav.active]);
  const { data: feeCtx } = useFeeContext(custno, locno);
  // The after-hours fee follows the VISIT, not the clock at booking (34ge):
  // it applies when the call is booked into After Hours. Before, anything
  // booked after 5 pm or on a weekend — even tomorrow at 10 — got the
  // after-hours rate. Members keep their rate either way (resolve_fee).
  const afterHours = win === "AFTER";
  const feeArgs = { downtown: feeCtx?.downtown ?? false, afterHours, segment: feeCtx?.segment ?? "residential" };
  const { data: memberFee } = useResolveFee(selected?.fee_code, { ...feeArgs, plus: true });
  const { data: standardFee } = useResolveFee(selected?.fee_code, { ...feeArgs, plus: false });
  const pay = isPlus ? memberFee : standardFee;
  const resolvedFee = pay?.amount ?? null;
  const savings = memberFee && standardFee ? Math.max(0, Number(standardFee.amount) - Number(memberFee.amount)) : 0;
  const effectiveFee = feeOverride.trim() !== "" ? Number(feeOverride) : resolvedFee;

  const onSelectType = (t: ServiceType) => {
    setTypeId(t.id);
    setTypeQuery(t.name);
    setComboOpen(false);
    setTypeSuggested(false); // a deliberate pick is no longer a "suggestion"
    // Only fill the service type's default note when the field is EMPTY — never
    // clobber an AI summary (chat/web-form) or anything the dispatcher typed
    // (Seth 2026-06-25 bug: picking a type wiped the prefilled summary).
    setDescription((cur) => (cur.trim() ? cur : (t.default_description ?? "")));
    // The customer's stated urgency (from the intake) wins over the type default.
    if (!prefillUrgency) setUrgency(t.default_urgency);
    setFeeOverride("");
  };

  const techName = (e: string) => {
    const t = (techs ?? []).find((x) => x.empno === e);
    return t ? t.empname || `${t.firstname ?? ""} ${t.lastname ?? ""}`.trim() || e : e;
  };

  // ── Customer tech preferences (from the 360 "Access & preferences" signals) ──
  // Confirmed = operational truth (auto-prefill the preferred tech). Proposed =
  // AI-found in the notes (we INFORM, but never auto-pick from a guess). We WARN
  // on a likely mistake, never block — the dispatcher always decides.
  const { data: custSignals } = useCustomerSignals(custno ?? "");
  // Resolve the customer's prefs against the roster (match a name on first name).
  const prefs = useMemo(() => {
    const rows = (custSignals ?? []).filter((s) => !s.archived_at && !s.hidden_at && (s.status === "confirmed" || s.status === "proposed"));
    const roster = techs ?? [];
    const matchTech = (raw: string) => {
      const first = raw.replace(/^(not|no|avoid|never send|don'?t send|do not send)\s+/i, "").trim().split(/\s+/)[0]?.toLowerCase();
      if (!first) return null;
      return roster.find((t) => (t.firstname ?? "").toLowerCase() === first)
        ?? roster.find((t) => techName(t.empno).toLowerCase().startsWith(first)) ?? null;
    };
    // A preferred signal can name MORE THAN ONE acceptable tech ("Jeremy or
    // Camron", Seth 2026-06-30) — match ALL roster techs in the value so each
    // gets a crown, not just the first word.
    const matchAllTechs = (raw: string) => {
      const found: typeof roster = [];
      const hay = ` ${raw.toLowerCase()} `;
      for (const t of roster) {
        const first = (t.firstname ?? "").toLowerCase();
        if (first && new RegExp(`\\b${first}\\b`).test(hay) && !found.includes(t)) found.push(t);
      }
      return found;
    };
    const preferredList: { empno: string | null; name: string; confirmed: boolean; src: string; sigId: string }[] = [];
    const excluded: { empno: string | null; name: string; confirmed: boolean; src: string }[] = [];
    let senior: { confirmed: boolean; src: string; label: string; sigId: string } | null = null;
    for (const s of rows) {
      if (s.signal_type === "tech_pref") {
        const isExclude = !!(s.detail && (s.detail as Record<string, unknown>).exclude) || /^(not|no|avoid|don'?t|do not)\b/i.test(s.value);
        const confirmed = s.status === "confirmed";
        const src = s.source_text ?? s.value;
        if (isExclude) {
          const m = matchTech(s.value);
          excluded.push({ empno: m?.empno ?? null, name: m ? techName(m.empno) : s.value, confirmed, src });
        } else {
          const matches = matchAllTechs(s.value);
          if (matches.length) {
            for (const m of matches) {
              const i = preferredList.findIndex((p) => p.empno === m.empno);
              if (i < 0) preferredList.push({ empno: m.empno, name: techName(m.empno), confirmed, src, sigId: s.id });
              else if (confirmed && !preferredList[i].confirmed) preferredList[i] = { empno: m.empno, name: techName(m.empno), confirmed, src, sigId: s.id };
            }
          } else if (!preferredList.some((p) => p.name === s.value)) {
            preferredList.push({ empno: null, name: s.value, confirmed, src, sigId: s.id });
          }
        }
      } else if (s.signal_type === "skill_req" && /senior|master|experienced|lead|licensed/i.test(s.value)) {
        if (!senior || (s.status === "confirmed" && !senior.confirmed)) senior = { confirmed: s.status === "confirmed", src: s.source_text ?? s.value, label: s.value, sigId: s.id };
      }
    }
    // `preferred` (singular) stays for the auto-prefill — prefer a CONFIRMED one so
    // we never auto-pick a tech from an unconfirmed guess.
    const preferred = preferredList.find((p) => p.confirmed) ?? preferredList[0] ?? null;
    return { preferred, preferredList, excluded, senior };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custSignals, techs]);

  // Auto-prefill the CONFIRMED preferred tech once (an unconfirmed AI guess never auto-picks).
  const didPrefill = useRef(false);
  useEffect(() => {
    if (!didPrefill.current && prefs.preferred?.confirmed && prefs.preferred.empno && !empno) {
      setEmpno(prefs.preferred.empno);
      didPrefill.current = true;
    }
  }, [prefs.preferred, empno]);

  // DEFAULT TECHS (Seth 2026-09-23: "for customers without preferences it's
  // Any but when there is a request of a specific tech or techs" the grid
  // opens on them). A CONFIRMED request starts the Tech row on those techs —
  // all of them for "Wayne or Miles" — and Any is one click away. A request
  // only FOUND IN THE NOTES is asked about first (the card above the chips):
  // a wrong default would hide every other tech's openings, and a proposed
  // signal is a question, not a fact (Seth 2026-08-11).
  const confirmedPick = useMemo(
    () => prefs.preferredList.filter((p) => p.confirmed && p.empno && !prefs.excluded.some((e) => e.empno === p.empno)).map((p) => p.empno as string),
    [prefs],
  );
  useEffect(() => {
    if (techPickTouched.current || confirmedPick.length === 0) return;
    setTechPick((cur) => (cur.length ? cur : confirmedPick));
  }, [confirmedPick]);
  const resolveSignal = useResolveSignal();
  const askPref = useMemo(() => {
    const open = prefs.preferredList.filter((p) => !p.confirmed && p.empno && !confirmedPick.includes(p.empno));
    if (open.length === 0) return null;
    return {
      empnos: open.map((p) => p.empno as string),
      ids: [...new Set(open.map((p) => p.sigId))],
      names: open.map((p) => techName(p.empno as string).split(" ")[0]).join(" or "),
      src: open[0].src,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs, confirmedPick]);
  const answerAsk = (yes: boolean) => {
    if (!askPref) return;
    for (const id of askPref.ids) resolveSignal.mutate({ id, status: yes ? "confirmed" : "dismissed" });
    if (yes) {
      techPickTouched.current = true;
      setTechPick(askPref.empnos);
    }
  };

  const techBadge = useMemo(() => {
    const c = prefs.preferredList.find((p) => p.confirmed) ?? prefs.preferredList[0] ?? null;
    return c ? { label: c.name.split(/\s+/)[0], confirmed: c.confirmed } : null;
  }, [prefs.preferredList]);
  const heroPhone = reached.confirm
    ? `${reached.confirm.name ? `${reached.confirm.name.split(/\s+/)[0]} ` : ""}${prettyPhone(reached.confirm.phone)}`
    : phone ? prettyPhone(phone) : null;

  // ── SKILL-STEER-01: who can take this call (Seth 2026-09-23) ──────────────
  // Three visible sources, all edited in Settings → Skills & Levels: the call
  // type's lowest level + specialties, the specialty of the unit the call is
  // about (the equipment on file), and a confirmed "senior tech" request. The
  // grid offers the techs who fit, first choices first; the Tech chips still
  // pick anyone, and a mismatch is flagged, never blocked.
  const levelLabels = useLevelLabels();
  const { data: quals } = useTechQuals();
  const { data: specialties } = useSpecialties();
  const { data: eqCats } = useEquipmentCategories();
  const callUnits = useCallEquipment(custno, locno);
  // Booking a queued ticket: its service type still says what the call is.
  const ticketTypeId = (existingTicket as unknown as { service_type_id?: string | null } | null)?.service_type_id ?? null;
  const typeForNeeds = selected ?? (ticketTypeId ? (types ?? []).find((t) => t.id === ticketTypeId) ?? null : null);
  const specName = (id: number) => (specialties ?? []).find((x) => x.id === id)?.name ?? "that specialty";
  // The unit the call is about: applied on its own when it's the only kind on
  // file for the call (a mini-split-only home); asked when there's a mix.
  const [equipOn, setEquipOn] = useState<Set<string>>(() => new Set());
  const [equipOff, setEquipOff] = useState<Set<string>>(() => new Set());
  const equipAll = useMemo(() => equipmentNeeds(systemsForType(typeForNeeds), callUnits ?? [], eqCats ?? []), [typeForNeeds, callUnits, eqCats]);
  const equipActive = useMemo(() => equipAll.filter((n) => (n.auto ? !equipOff.has(n.key) : equipOn.has(n.key))), [equipAll, equipOn, equipOff]);
  const equipAsk = useMemo(() => equipAll.filter((n) => !equipActive.includes(n)), [equipAll, equipActive]);
  const toggleEquip = (key: string, on: boolean) => {
    const n = equipAll.find((x) => x.key === key);
    if (!n) return;
    const flip = (cur: Set<string>, add: boolean) => { const x = new Set(cur); if (add) x.add(key); else x.delete(key); return x; };
    if (n.auto) setEquipOff((cur) => flip(cur, !on));
    else setEquipOn((cur) => flip(cur, on));
  };
  // The customer's own requirement that names a specialty ("Mini Split Tech"
  // in the notes): confirmed = a need on every call here; found in the notes =
  // asked, like a tech request (a proposed signal is a question, not a fact).
  const custSpecs = useMemo(() => {
    const norm = (x: string) => x.toLowerCase().replace(/[^a-z0-9]/g, "");
    const out: { sigId: string; specId: number; label: string; confirmed: boolean }[] = [];
    for (const sg of custSignals ?? []) {
      if (sg.signal_type !== "skill_req" || sg.archived_at || sg.hidden_at || (sg.status !== "confirmed" && sg.status !== "proposed")) continue;
      const v = norm(sg.value);
      const sp = (specialties ?? []).find((x) => x.active && norm(x.name).length >= 4 && v.includes(norm(x.name)));
      if (sp && !out.some((o) => o.specId === sp.id)) out.push({ sigId: sg.id, specId: sp.id, label: sg.value, confirmed: sg.status === "confirmed" });
    }
    return out;
  }, [custSignals, specialties]);
  const needs = useMemo(() => {
    // An archived specialty stops being a need anywhere.
    const live = (id: number) => !specialties || specialties.some((x) => x.id === id && x.active);
    const skills: { id: number; why: string; equipKey: string | null; from: "type" | "equipment" | "customer" }[] = [];
    for (const id of typeForNeeds?.needs_skills ?? []) if (live(id) && !skills.some((x) => x.id === id)) skills.push({ id, why: `${typeForNeeds!.name} calls`, equipKey: null, from: "type" });
    for (const n of equipActive) if (n.skillId != null && live(n.skillId) && !skills.some((x) => x.id === n.skillId)) skills.push({ id: n.skillId, why: `the ${n.label} on file`, equipKey: n.key, from: "equipment" });
    for (const c of custSpecs) if (c.confirmed && !skills.some((x) => x.id === c.specId)) skills.push({ id: c.specId, why: `the customer's record (“${c.label}”)`, equipKey: null, from: "customer" });
    const typeMin = typeForNeeds?.min_level ?? 0;
    const seniorMin = prefs.senior?.confirmed ? 3 : 0;
    const minLevel = Math.max(typeMin, seniorMin) || null;
    const levelWhy = !minLevel ? null : seniorMin && seniorMin >= typeMin ? "the customer asked for a senior tech" : `${typeForNeeds?.name ?? "This call type"} calls`;
    const offerFirst: string[] = [];
    for (const e of [...(typeForNeeds?.offer_first ?? []), ...equipActive.flatMap((n) => n.offerFirst)]) if (!offerFirst.includes(e)) offerFirst.push(e);
    return { skills, minLevel, levelWhy, offerFirst };
  }, [typeForNeeds, equipActive, prefs.senior, specialties, custSpecs]);
  const custSpecAsk = custSpecs.filter((c) => !c.confirmed && !needs.skills.some((x) => x.id === c.specId));
  const hasNeeds = !!needs.minLevel || needs.skills.length > 0;
  const whyNot = (e: string) => missReasons(quals?.get(e), needs.minLevel, needs.skills.map((x) => x.id), levelLabels, specName);
  const qualified = useMemo(
    () => (hasNeeds && quals ? (techs ?? []).filter((t) => missReasons(quals.get(t.empno), needs.minLevel, needs.skills.map((x) => x.id), levelLabels, () => "").length === 0).map((t) => t.empno) : []),
    [hasNeeds, quals, techs, needs, levelLabels],
  );
  const isFit = (e: string) => !hasNeeds || !quals || qualified.includes(e);
  // Nobody on the roster fits → the grid shows everyone (and says so).
  const allowEmpnos = hasNeeds && quals && qualified.length > 0 ? qualified : null;
  /** "Service Tech or higher + Mini-Split" — what the call needs, in words. */
  const needText = [needs.minLevel ? levelNeedText(levelLabels, needs.minLevel) : "", ...needs.skills.map((x) => specName(x.id))].filter(Boolean).join(" + ");
  const answerSenior = (yes: boolean) => {
    if (prefs.senior) resolveSignal.mutate({ id: prefs.senior.sigId, status: yes ? "confirmed" : "dismissed" });
  };

  // Booking an existing ticket: prefill the schedule form from it once, and turn
  // on the route copilot so the board shows where it fits the moment it opens.
  const didTicketPrefill = useRef(false);
  useEffect(() => {
    if (!existingTicket || didTicketPrefill.current) return;
    didTicketPrefill.current = true;
    setDescription(existingTicket.notes ?? "");
    setPo(existingTicket.po_number ?? "");
    if (existingTicket.urgency) setUrgency(existingTicket.urgency);
    if (existingTicket.affinity_empno) setEmpno(existingTicket.affinity_empno);
    if (existingTicket.target_date) setDate(existingTicket.target_date);
    if (existingTicket.target_window) { setWin(existingTicket.target_window); winTouched.current = true; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingTicket]);

  const routineCap = useOrgSettingNumber("routine_cap_per_day", DEFAULT_ROUTINE_CAP).data ?? DEFAULT_ROUTINE_CAP;
  const { data: coords } = useQuery({
    queryKey: ["loc_coords", custno, locno],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.from("mirror_location").select("latitude, longitude").eq("custno", custno!).eq("locno", locno ?? "00001").limit(1).maybeSingle();
      const g = data as { latitude: number | null; longitude: number | null } | null;
      return { lat: g?.latitude ?? null, lng: g?.longitude ?? null };
    },
  });
  // In booking mode the ticket already carries its geocode; fall back to the
  // location lookup for create mode.
  const ticketCoords =
    existingTicket && existingTicket.lat != null && existingTicket.lng != null
      ? { lat: existingTicket.lat, lng: existingTicket.lng }
      : null;
  const effCoords = ticketCoords ?? coords ?? null;
  const hasCoords = effCoords?.lat != null && effCoords?.lng != null;

  // ── SCHED-FLOW-01: the availability grid ──────────────────────────────────
  const todayISO = isoOf(new Date());
  const includeSat = avail.days.has(6);
  const gridDays = useMemo(() => buildGridDays(todayISO, weekOffset, includeSat), [todayISO, weekOffset, includeSat]);
  // Every window is scored; a window the customer can't do is crossed off on
  // the grid (instant, no rescoring) and skipped by the best-fit read.
  const gridWindows = useMemo(() => (isInstall ? INSTALL_WINDOWS : SERVICE_WINDOWS.slice()), [isInstall]);
  // Days off = single dates the dispatcher crossed + every date of a weekday
  // the customer can't do.
  const crossedAll = useMemo(() => {
    const out = new Set(crossed);
    for (const d of gridDays) { const dow = dowOf(d); if (dow >= 1 && dow <= 5 && !avail.days.has(dow)) out.add(d); }
    return out;
  }, [crossed, gridDays, avail.days]);
  const windowLabel = (w: string) => (isInstall ? INSTALL_WINDOW_LABELS[w] ?? w : WINDOW_LABEL[w] ?? w);
  const techFirst = (e: string) => techName(e).split(" ")[0] || e;
  // Evening & After Hours fold (Seth 2026-09-23: 3–7 and after hours "are
  // rarely used"). Closed by default; opens itself when the pick lands there
  // or the customer can't do any daytime window.
  const [laterOpen, setLaterOpen] = useState(false);
  useEffect(() => { if (win === "3-7" || win === "AFTER") setLaterOpen(true); }, [win]);
  useEffect(() => {
    if (["8AM", "8-12", "10-2", "12-4"].every((w) => avail.windowsOff.has(w))) setLaterOpen(true);
  }, [avail.windowsOff]);
  // After Hours = that night's on-call pair, from the published calendar.
  const oncallQ = useOncallDaysPublished(gridDays[0] ?? todayISO, gridDays[gridDays.length - 1] ?? todayISO, step === 2 && !isInstall);
  const oncallByDay = useMemo(
    () => new Map((oncallQ.data ?? []).map((d) => [d.day, { primary: d.primary_empno, backup: d.backup_empno }])),
    [oncallQ.data],
  );
  const pickAfterHours = (e: string, d: string) => {
    winTouched.current = true;
    setEmpno(e);
    setDate(d);
    setWin("AFTER");
    setExactTime(null);
    setSelectedOpt(null);
  };
  // A "senior tech" read from the notes (not confirmed yet) leans the grid to
  // the Senior Techs without filtering anyone out.
  const seniorLean = useMemo(
    () => (prefs.senior && !prefs.senior.confirmed && quals ? [...quals.entries()].filter(([, q]) => q.level === 3).map(([e]) => e) : []),
    [prefs.senior, quals],
  );
  const gridKey = `${gridDays.join(",")}|${gridWindows.join(",")}|${techPick.join("+")}|${urgency}|${routineCap}|${(techs ?? []).length}|${prefs.preferredList.map((p) => p.empno).join(",")}|fit:${(allowEmpnos ?? []).join(",")}|first:${needs.offerFirst.join(",")}|lean:${seniorLean.join(",")}`;
  useEffect(() => {
    if (step !== 2 || !hasCoords || !custno) return;
    let cancelled = false;
    setGridLoading(true);
    (async () => {
      try {
        const job = {
          id: existingTicket?.id ?? "new", custno, locno, lat: effCoords!.lat, lng: effCoords!.lng,
          summary: description || selected?.name || "", notes: null, urgency,
          affinity_empno: existingTicket?.affinity_empno ?? null, lane_key: "incoming",
          target_date: existingTicket?.target_date ?? null,
        } as unknown as PendingTicket;
        const roster = (techs ?? []).map((x) => ({ empno: x.empno, first: x.firstname }));
        // First in a cell when they fit: the customer's requested techs, then
        // the call's "offer first" techs (Settings → Skills & Levels), then —
        // for a senior-tech request still only in the notes — the Senior Techs.
        const preferEmpnos: string[] = [];
        for (const e of [...prefs.preferredList.map((p) => p.empno), ...needs.offerFirst, ...seniorLean]) if (e && !preferEmpnos.includes(e)) preferEmpnos.push(e);
        // Only the techs who fit the call are scored — unless the dispatcher
        // picked techs on the Tech row, which always wins.
        const r = await availabilityGrid(job, roster, techName, { days: gridDays, windows: gridWindows, urgency, routineCap, preferEmpnos, onlyEmpnos: techPick.length ? techPick : null, allowEmpnos });
        if (!cancelled) setGrid(r);
      } catch {
        if (!cancelled) setGrid(null);
      } finally {
        if (!cancelled) setGridLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, hasCoords, custno, locno, gridKey]);

  // A tech + day that arrived with the ticket or the board's double-click is
  // pre-selected on the grid the moment it loads (its best window).
  useEffect(() => {
    if (!grid || selectedOpt || !empno || !date) return;
    for (const w of gridWindows) {
      const o = (grid.cells.get(cellKey(date, w)) ?? []).find((x) => x.empno === empno);
      if (o) { setSelectedOpt(o); if (!winTouched.current) setWin(o.window); return; }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid]);

  // SCHED-AVAIL-01: what's ON FILE about when we can come — shown as hints
  // with a Use button, never applied on their own (Seth 2026-09-17: "nothing
  // should be preselected"). Ours (window_pref, structured) and the
  // extractor's time-of-day / weekday reads, newest first.
  const onFile = useMemo(() => {
    const rows = (custSignals ?? []).filter((x) => !x.archived_at && !x.hidden_at && x.status !== "dismissed" && (x.signal_type === "window_pref" || (x.status === "confirmed" && TIME_TALK.test(x.value))));
    return rows
      .sort((a, b) => (b.confirmed_at ?? b.created_at ?? "").localeCompare(a.confirmed_at ?? a.created_at ?? ""))
      .slice(0, 3)
      .map((sig) => ({ sig, parsed: availabilityFromSignal(sig), when: (sig.confirmed_at ?? sig.created_at ?? "").slice(0, 10) }));
  }, [custSignals]);
  // Use = ADD what's on file to what the dispatcher has (windows off join,
  // days replace only when the file names days, the note only when blank).
  const applyOnFile = (parsed: Partial<Availability>) =>
    setAvail((a) => ({
      days: parsed.days ? new Set(parsed.days) : a.days,
      windowsOff: parsed.windowsOff ? new Set([...a.windowsOff, ...parsed.windowsOff]) : a.windowsOff,
      note: a.note.trim() ? a.note : parsed.note ?? a.note,
    }));
  // A pick that a later cross-off makes impossible lets go by itself.
  useEffect(() => {
    if (selectedOpt && (crossedAll.has(selectedOpt.dateISO) || avail.windowsOff.has(selectedOpt.window))) { setSelectedOpt(null); setDate(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crossedAll, avail.windowsOff]);

  // The copilot's best tech-days for the urgency — offered, never forced: the
  // three best distinct tech+day cells in view (the picked one always among
  // them). Routine also tags the lightest open days so the reading is visible.
  const topOptions = useMemo(() => {
    if (!grid) return [] as TimeSuggestion[];
    const dayIdx = (d: string) => gridDays.indexOf(d);
    const score = (o: TimeSuggestion) =>
      urgency === "asap" ? dayIdx(o.dateISO) * 100 + o.addedMiles
        : urgency === "routine" ? (o.stopCount + o.maintCount) * 10 + o.addedMiles + dayIdx(o.dateISO) * 0.1
          : o.addedMiles + 0.6 * o.stopCount + 1.2 * dayIdx(o.dateISO);
    const best = new Map<string, TimeSuggestion>();
    for (const d of gridDays) {
      if (crossedAll.has(d)) continue;
      for (const w of gridWindows) {
        if (avail.windowsOff.has(w)) continue;
        const o = grid.cells.get(cellKey(d, w))?.[0];
        if (!o) continue;
        const k = `${o.empno}|${o.dateISO}`;
        const cur = best.get(k);
        if (!cur || score(o) < score(cur)) best.set(k, o);
      }
    }
    const list = [...best.values()].sort((a, b) => score(a) - score(b));
    const top = list.slice(0, 3);
    if (selectedOpt && !top.some((o) => o.empno === selectedOpt.empno && o.dateISO === selectedOpt.dateISO && o.window === selectedOpt.window)) {
      return [selectedOpt, ...top.filter((o) => !(o.empno === selectedOpt.empno && o.dateISO === selectedOpt.dateISO))].slice(0, 3);
    }
    return top;
  }, [grid, gridDays, gridWindows, crossedAll, avail.windowsOff, urgency, selectedOpt]);
  const lightDays = useMemo(() => {
    if (!grid || urgency !== "routine") return undefined;
    const load: [string, number][] = [];
    for (const d of gridDays) {
      if (crossedAll.has(d)) continue;
      let best = Infinity;
      for (const w of gridWindows) {
        if (avail.windowsOff.has(w)) continue;
        const o = grid.cells.get(cellKey(d, w))?.[0];
        if (o) best = Math.min(best, o.stopCount + o.maintCount);
      }
      if (best < Infinity) load.push([d, best]);
    }
    load.sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]));
    return new Set(load.slice(0, 3).map(([d]) => d));
  }, [grid, gridDays, gridWindows, crossedAll, avail.windowsOff, urgency]);

  const pickOption = (o: TimeSuggestion) => {
    setEmpno(o.empno);
    setDate(o.dateISO);
    setExactTime(null);
    setSelectedOpt(o);
    winTouched.current = true; // the row IS the promised window
    setWin(o.window);
  };
  const toggleCrossed = (d: string) => {
    const dow = dowOf(d);
    // A day crossed by its WEEKDAY comes back with the whole weekday.
    if (dow >= 1 && dow <= 5 && !avail.days.has(dow)) { toggleDow(dow); return; }
    setCrossed((prev) => { const n = new Set(prev); if (n.has(d)) n.delete(d); else n.add(d); return n; });
    if (selectedOpt?.dateISO === d && !crossed.has(d)) { setSelectedOpt(null); setDate(""); }
  };
  // Days chips: from "any day", clicking a weekday means ONLY that day (the
  // customer said "Tuesdays"); more clicks add or remove days; Saturday is an
  // add-on either way; Any day resets. Never fewer than one day.
  const toggleDow = (dow: number) =>
    setAvail((a) => {
      const next = new Set(a.days);
      if (dow === 6) { if (next.has(6)) next.delete(6); else next.add(6); }
      else if (!isRestrictedDays(a.days)) { next.clear(); next.add(dow); }
      else if (next.has(dow)) { next.delete(dow); if ([...next].every((x) => x === 6)) return { ...a, days: DEFAULT_DAYS() }; }
      else next.add(dow);
      return { ...a, days: next };
    });
  const resetDays = () => setAvail((a) => ({ ...a, days: DEFAULT_DAYS() }));
  const toggleWindow = (w: string) =>
    setAvail((a) => { const n = new Set(a.windowsOff); if (n.has(w)) n.delete(w); else n.add(w); return { ...a, windowsOff: n }; });
  // By hand: the three inputs still work; a matching grid cell lights up when there is one.
  const syncByHand = (e: string, d: string, w: string) => {
    const o = grid && d && e ? (grid.cells.get(cellKey(d, w)) ?? []).find((x) => x.empno === e) ?? null : null;
    setSelectedOpt(o);
  };
  // Dragging the token to a precise minute: lock the tech+day, set the exact time
  // and derive the customer-facing window from it (Seth 2026-06-23, slice 2).
  const onCommitTime = (sug: TimeSuggestion, hour: number) => {
    winTouched.current = true; // a deliberate drop = a deliberate window
    setEmpno(sug.empno);
    setDate(sug.dateISO);
    setWin(winForHour(hour));
    setExactTime(hourToHHMM(hour));
  };
  // Any explicit window pick — including "Anytime" (flexible, no specific promise)
  // — STICKS and is never re-derived (Seth 2026-06-24: picking Anytime was snapping
  // to 3-7). "Anytime" books a flexible window; the card sits at the route slot.
  const onPickWindow = (w: string) => {
    winTouched.current = true;
    setWin(w);
  };

  const callNumber = reached.headsUp?.phone ?? "";
  const callMethod: "call" | "text" = reached.headsUp?.method ?? "call";
  const methodLabel = callMethod === "text" ? "Text" : "Call";
  const baseInput = () => ({
    custno, locno, customerName, address, city,
    // The confirmation text (and the day-before reminder) go to the chosen line.
    phone: reached.confirm?.phone ?? phone,
    contactName: reached.confirm?.name ?? null,
    contactEmail: reached.email?.email ?? null,
    summary: description.trim() || selected!.name,
    urgency, laneKey: "incoming",
    targetWindow: win, targetDate: date || null,
    notes: callNumber ? `${methodLabel} on the way: ${callNumber}` : null,
    serviceTypeId: selected!.id, serviceTypeName: selected!.name,
    diagFee: effectiveFee ?? null, diagFeeOverridden: feeOverride.trim() !== "",
    poNumber: po,
    sourceCallId: sourceCallId ?? null,
    source: sourceCallId ? "call" : undefined,
  });

  const [actionError, setActionError] = useState<string | null>(null);
  // SCHED-AVAIL-01: what the customer said about when we can come goes on the
  // record WITH THE DATE (a window_pref watch-out: 360 rail, board, tech app;
  // next booking shows it as a hint with Use). Single crossed dates are about
  // this visit only and are not kept. Best-effort, never fails the booking.
  const addSignal = useAddSignal();
  // "Wayne or Miles is fine," said on the phone → one click puts it on the
  // record (a confirmed request, dated); older tech requests step aside (on
  // file, switched off) and the next booking opens on these techs.
  const [prefSaved, setPrefSaved] = useState(false);
  const pickDiffers = techPick.length > 0 && (techPick.length !== confirmedPick.length || techPick.some((e) => !confirmedPick.includes(e)));
  // TECH-REQUEST-EDIT: one shared writer (techRequest.ts) with Edit customer
  // details and the Watch For editor — it also revives a switched-off "Jeremy"
  // instead of tripping the dedupe index, which the old insert did silently.
  const [prefSaving, setPrefSaving] = useState(false);
  const savePickAsPref = async () => {
    if (!custno || techPick.length === 0 || prefSaving) return;
    setPrefSaving(true);
    try {
      await setTechRequest({
        custno, source: "schedule", signals: custSignals,
        picks: techPick.map((e) => ({ empno: e, first: techName(e).split(" ")[0] })),
      });
      qc.invalidateQueries({ queryKey: ["customer_signals", custno] });
      setPrefSaved(true);
    } catch (e) {
      onErrToast("Couldn't save the tech request")(e); // the booking matters more — the Watch-outs editor can add it
    } finally { setPrefSaving(false); }
  };
  const availText = availabilityText(avail, windowLabel);
  const stripSaid = (v: string) => v.replace(/\s+—\s+said\s.+$/, "").trim();
  const onFileSame = onFile.find((o) => (o.sig.detail as { source?: string } | null)?.source === "schedule" && stripSaid(o.sig.value) === availText) ?? null;
  const willSave = !!availText && (onFileSame ? resave : remember);
  const persistAvailability = async () => {
    if (!willSave || !availText || !custno) return;
    try {
      const said = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      await addSignal.mutateAsync({
        custno, locno: null, signal_type: "window_pref",
        value: `${availText} — said ${said}`,
        detail: { source: "schedule", days: [...avail.days].sort(), windowsOff: [...avail.windowsOff], note: avail.note.trim() },
      });
      // The newest statement is the record; older scheduler captures step
      // aside (still on file, switched off — the editor can bring one back).
      const stale = (custSignals ?? []).filter((x) => x.signal_type === "window_pref" && !x.hidden_at && (x.detail as { source?: string } | null)?.source === "schedule");
      if (stale.length > 0) {
        const { data: u } = await supabase.auth.getUser();
        await supabase.from("extracted_signal").update({ hidden_at: new Date().toISOString(), hidden_by: u.user?.email ?? null, updated_at: new Date().toISOString() }).in("id", stale.map((x) => x.id));
        qc.invalidateQueries({ queryKey: ["customer_signals", custno] });
      }
    } catch { /* the visit is what matters; the Watch-outs editor can add it later */ }
  };
  // Best-effort: the booking never fails because a contact didn't save.
  const persistContact = async () => {
    if (!custno) return;
    await persistReach(custno, locno, people, reach, qc);
  };
  const scheduleNow = async () => {
    if (!empno || !date) return;
    setActionError(null);
    try {
    // Booking an existing queued ticket: persist any note/urgency edits, then flip
    // it pending→scheduled in place (no new dispatch). Same schedule write-path.
    if (booking) {
      await update.mutateAsync({ id: existingTicket!.id, notes: description.trim() || null, urgency, po_number: po.trim() || null });
      await schedule.mutateAsync({
        id: existingTicket!.id, assignedEmpno: empno, scheduledDate: date, scheduledWindow: win,
        scheduledTime: exactTime,
        callAhead: !!callNumber, callAheadPhone: callNumber || null, callAheadMethod: callMethod, callAheadName: reached.headsUp?.name ?? null,
        phone: reached.confirm?.phone ?? existingTicket!.phone ?? null,
        contactName: reached.confirm?.name ?? null, contactEmail: reached.email?.email ?? null,
      });
      await uploadStaged(existingTicket!.id);
      await persistContact();
      await persistAvailability();
      setOutcome({ mode: "scheduled", dispatchId: existingTicket!.id });
      return;
    }
    if (!selected) return;
    const created = await create.mutateAsync(baseInput());
    await schedule.mutateAsync({
      id: created.id, assignedEmpno: empno, scheduledDate: date, scheduledWindow: win,
      scheduledTime: exactTime,
      callAhead: !!callNumber, callAheadPhone: callNumber || null, callAheadMethod: callMethod, callAheadName: reached.headsUp?.name ?? null,
    });
    await uploadStaged(created.id);
    await persistContact();
    await persistAvailability();
    setOutcome({ mode: "scheduled", dispatchId: created.id });
    } catch {
      // 360-AUDIT #3: a silent failure here = the dispatcher retries and
      // double-books. Say it, and say what may already exist.
      setActionError("Booking didn't finish — nothing may be scheduled, but a queue ticket MIGHT exist. Check the pending list before retrying.");
    }
  };
  const saveToQueue = async () => {
    if (!selected) return;
    setActionError(null);
    try {
      const created = await create.mutateAsync(baseInput());
      await uploadStaged(created.id);
      await persistContact();
      await persistAvailability();
      setOutcome({ mode: "queued", dispatchId: created.id });
    } catch {
      setActionError("Couldn't save the ticket — try again.");
    }
  };

  const prettyDate = date ? new Date(date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "";
  // Warn (never block) when the picked tech conflicts with a customer preference.
  const selExcluded = empno ? prefs.excluded.find((e) => e.empno === empno) ?? null : null;
  // SKILL-STEER-01: the picked tech doesn't fit the call (level or specialty).
  // Flagged here when he came from somewhere other than the Tech row (the grid
  // only offers fits; the on-call row and By hand don't filter) — the Tech row
  // flags its own picks above the grid.
  const selMiss = empno && hasNeeds && quals && !techPick.includes(empno) ? whyNot(empno) : [];
  const seniorMiss = !!prefs.senior && !prefs.senior.confirmed && !!empno && !!quals && quals.get(empno)?.level !== 3;
  const busy = create.isPending || schedule.isPending || update.isPending;

  // LEAVE-GUARD (34i87, Seth: "I was almost at completion … clicked out to
  // test and lost all the info"). Outside-click and Esc still close an
  // UNTOUCHED window at once; once there is work in it they ask first.
  const seedEmpno = prefillEmpno ?? existingTicket?.affinity_empno ?? "";
  const seedDate = prefillDate ?? existingTicket?.target_date ?? "";
  const descSeeds = [initialDescription, selected?.default_description ?? "", callSeedRef.current].map((x) => (x ?? "").trim());
  const dirty =
    step > 1 ||
    (!booking && !!selected && !typeSuggested) ||
    (description.trim() !== "" && !descSeeds.includes(description.trim())) ||
    empno !== seedEmpno || date !== seedDate ||
    staged.length > 0 || po.trim() !== "" || feeOverride.trim() !== "" ||
    !!reach.other || !!reach.headsUpKey || !!reach.emailKey || Object.keys(reach.addedEmail).length > 0;
  const [leaveOpen, setLeaveOpen] = useState(false);
  const requestClose = () => {
    if (dirty && !outcome) setLeaveOpen(true);
    else onClose();
  };

  if (outcome) {
    return (
      <Modal open onClose={onClose} title={outcome.mode === "scheduled" ? "Scheduled" : "Saved to queue"} size="sm">
        <div className="space-y-4 text-center">
          <div className={cn("mx-auto flex h-12 w-12 items-center justify-center rounded-full", outcome.mode === "scheduled" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600")}>
            {outcome.mode === "scheduled" ? <Check className="h-6 w-6" /> : <Inbox className="h-6 w-6" />}
          </div>
          {outcome.mode === "scheduled" ? (
            <div className="text-sm text-slate-700">
              <p className="font-semibold text-slate-900">{techName(empno)}</p>
              <p>{prettyDate} · {win}</p>
              {reachSummary(reached).map((l) => <p key={l} className="mt-1 text-xs text-slate-500">{l}</p>)}
              <p className="mt-1 text-xs text-slate-500">It's on the board.</p>
            </div>
          ) : (
            <p className="text-sm text-slate-700">Saved to <strong>Incoming requests</strong>. Schedule it onto the board whenever you're ready.</p>
          )}
          <div className="flex items-center justify-center gap-2">
            {/* SCHED-PREVIEW-01: see it land — the ambient board window jumps to
                the day with the tech's column ringed (two-window model; falls
                back to the one named board tab). */}
            {outcome.mode === "scheduled" && date && (
              <Button variant="secondary" size="sm" onClick={() => showOnBoard(date, { tech: empno || undefined })}>
                <CalendarClock className="h-3.5 w-3.5" /> Show on board
              </Button>
            )}
            <Button size="sm" onClick={onClose}>Done</Button>
          </div>
        </div>
      </Modal>
    );
  }

  const stepLabel = ["What", "When & Who", "Confirm"];
  const feeText = selected ? (selected.fee_code ? (effectiveFee != null ? `$${effectiveFee} diagnostic` : "diagnostic fee") : "no diagnostic fee") : null;
  const urgencyText = URGENCY.find((u) => u.key === urgency)?.label ?? urgency;
  const whenText = empno && date ? `${techName(empno)} · ${prettyDate} · ${win}${exactTime ? ` · at ${hourToHHMM(hhmmToHour(exactTime)).replace(/^0/, "")}` : ""}` : null;
  const consChips: string[] = [
    availText,
    ...[...crossed].sort().map((d) => `not ${new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}`),
  ].filter(Boolean);
  const toldLines = reachSummary(reached);
  const requestLabel = selected?.name ?? existingTicket?.summary ?? null;
  const filesText = staged.length || (attachRequest && requestPhotoCount)
    ? [staged.length ? `${staged.length} file${staged.length === 1 ? "" : "s"}` : "", attachRequest && requestPhotoCount ? `${requestPhotoCount} customer photo${requestPhotoCount === 1 ? "" : "s"}` : ""].filter(Boolean).join(" · ")
    : null;
  const canNext1 = !!selected || booking;
  const canNext2 = !!empno && !!date;
  const canBook = (!!selected || booking) && !!empno && !!date && !busy && !dnsBlocked;

  // ── BOOK-A-CALL-REDESIGN: the window's band — the customer, not a title ──
  const hero = (
    <span className="flex min-w-0 items-center gap-3">
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-900 text-[15px] font-extrabold text-white">{initialsOf(customerName)}</span>
      <span className="block min-w-0">
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-[20px] font-extrabold leading-tight tracking-tight text-slate-900">{customerName}</span>
          {custno && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[11px] font-bold text-slate-500">{custno}</span>}
          {dnsBlocked && <span className="rounded-md bg-rose-600 px-2 py-0.5 text-[11px] font-bold text-white">Do Not Service</span>}
          {planLabel && <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700">{planLabel}</span>}
          {techBadge && <span className={cn("rounded-md border px-2 py-0.5 text-[11px] font-bold", techBadge.confirmed ? "border-violet-200 bg-violet-50 text-violet-700" : "border-dashed border-violet-300 bg-white text-violet-700")}>{techBadge.label}</span>}
        </span>
        <span className="mt-0.5 block truncate text-[12.5px] font-normal text-slate-500">
          <span className="font-semibold text-slate-700">{[address, city].filter(Boolean).join(", ") || "No address on file"}</span>
          {heroPhone && <> · {heroPhone}</>}
          {lastVisit && <> · last visit {lastVisit}</>}
        </span>
      </span>
    </span>
  );

  const stepsStrip = (
    <div className="grid grid-cols-3 gap-1.5">
      {stepLabel.map((label, i) => {
        const n = (i + 1) as 1 | 2 | 3;
        const done = n < step;
        const on = n === step;
        const reachable = n === 1 || (n === 2 && canNext1) || (n === 3 && canNext1 && canNext2);
        return (
          <button
            key={label}
            type="button"
            disabled={!reachable}
            onClick={() => reachable && setStep(n)}
            className={cn(
              "flex items-center gap-2.5 rounded-xl border px-3.5 py-2 text-left text-[13px] font-bold transition-colors",
              on ? "border-brand-500 bg-brand-50 text-brand-700" : done ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-hairline bg-white text-slate-400",
              reachable && !on && "hover:bg-slate-50",
            )}
          >
            <span className={cn("inline-grid h-5.5 w-5.5 place-items-center rounded-full text-[11px] font-extrabold", on ? "bg-brand-600 text-white" : done ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500")} style={{ width: 22, height: 22 }}>
              {done ? <Check className="h-3 w-3" /> : n}
            </span>
            {label}
          </button>
        );
      })}
    </div>
  );

  const footer = (
    <div className="flex items-center justify-between gap-2">
      <div>
        {step === 1 ? (
          <Button variant="secondary" size="sm" onClick={requestClose}>Cancel</Button>
        ) : (
          <Button variant="secondary" size="sm" onClick={() => setStep(step === 3 ? 2 : 1)}><ArrowLeft className="h-3.5 w-3.5" /> Back</Button>
        )}
      </div>
      <div className="flex items-center gap-2">
        {!booking && (
          <Button variant="secondary" size="sm" onClick={saveToQueue} disabled={!selected || busy || dnsBlocked} title="File it as a pending ticket — schedule it onto the board later">
            <Inbox className="h-3.5 w-3.5" /> Save to Queue
          </Button>
        )}
        {step === 1 && (
          <Button size="sm" onClick={() => setStep(2)} disabled={!canNext1}>Next: When &amp; Who <ArrowRight className="h-3.5 w-3.5" /></Button>
        )}
        {step === 2 && (
          <Button size="sm" onClick={() => setStep(3)} disabled={!canNext2}>Next: Confirm <ArrowRight className="h-3.5 w-3.5" /></Button>
        )}
        {step === 3 && (
          <Button size="sm" onClick={scheduleNow} disabled={!canBook}>
            <CalendarPlus className="h-3.5 w-3.5" /> {busy ? "Booking…" : "Schedule"}
          </Button>
        )}
      </div>
    </div>
  );

  const filesRow = custno ? (
    <FilesRow
      staged={staged}
      onAdd={() => fileRef.current?.click()}
      onRemove={(i) => setStaged((s) => s.filter((_, j) => j !== i))}
      requestPhotoCount={requestPhotoCount}
      requestWhen={requestMedia?.[0]?.created_at ?? null}
      attachRequest={attachRequest}
      onToggleAttach={() => setAttachRequest((v) => !v)}
    >
      <input
        ref={fileRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          const l = e.target.files;
          if (l && l.length) setStaged((s) => [...s, ...Array.from(l)]);
          if (fileRef.current) fileRef.current.value = "";
        }}
      />
    </FilesRow>
  ) : null;

  const urgencySeg = (
    <div className="inline-flex items-center gap-0.5 rounded-full border border-hairline bg-slate-50 p-0.5">
      {URGENCY.map((u) => (
        <button key={u.key} type="button" onClick={() => setUrgency(u.key)} aria-pressed={urgency === u.key} title={u.hint}
          className={cn("rounded-full px-3 py-1 text-[12.5px] font-bold transition-colors",
            urgency === u.key ? (u.key === "asap" ? "bg-red-100 text-red-700 shadow-sm" : u.key === "soon" ? "bg-white text-amber-700 shadow-sm" : "bg-white text-slate-800 shadow-sm") : "text-slate-500 hover:text-slate-800")}>
          {u.label}
        </button>
      ))}
    </div>
  );

  return (
    <Modal open onClose={requestClose} title={hero} size="2xl" footer={footer}>
      {leaveOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/30 p-4" onClick={() => setLeaveOpen(false)}>
          <div role="alertdialog" aria-labelledby="leave-title" className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 id="leave-title" className="text-base font-semibold text-slate-900">Leave without booking?</h3>
            <p className="mt-1 text-sm text-slate-500">What you've entered here goes away.</p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setLeaveOpen(false)} data-autofocus="">Keep Editing</Button>
              <Button size="sm" variant="danger" onClick={onClose}>Discard</Button>
            </div>
          </div>
        </div>
      )}
      <div className="space-y-4">
        {stepsStrip}
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="min-w-0 space-y-6">

            {/* ================= STEP 1 — WHAT ================= */}
            {step === 1 && (
              <>
                {sourceCaption && (
                  <div className="flex items-center gap-1.5 rounded-lg border border-brand-100 bg-brand-50/60 px-3 py-1.5 text-[11px] font-medium text-brand-700">
                    <Sparkles className="h-3.5 w-3.5 shrink-0" />
                    {sourceCaption} — review &amp; edit the notes below before saving
                  </div>
                )}

                {booking && (
                  <div>
                    <Label>Scheduling This Request</Label>
                    <div className="rounded-xl border border-hairline bg-white px-3.5 py-2.5 text-[14px] font-semibold text-slate-800">{existingTicket!.summary}</div>
                  </div>
                )}

                {!booking && (
                  <div className="relative">
                    <div className="mb-1.5 flex items-center gap-2">
                      <Label className="mb-0">Service Request</Label>
                      {typeSuggested && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10.5px] font-semibold text-amber-700">
                          <Sparkles className="h-3 w-3" /> AI suggested — confirm or change
                        </span>
                      )}
                    </div>
                    <div className="relative">
                      {selected ? <CatIcon cat={selected.category} className="absolute left-3 top-1/2 -translate-y-1/2" /> : <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />}
                      <input
                        value={typeQuery}
                        data-autofocus=""
                        role="combobox" aria-expanded={listOpen} aria-autocomplete="list" autoComplete="off"
                        onChange={(e) => { setTypeQuery(e.target.value); setComboOpen(true); if (selected) setTypeId(""); }}
                        onFocus={(e) => { setComboOpen(true); e.target.select(); }}
                        onKeyDown={typeNav.onKeyDown}
                        placeholder="What's the call?"
                        className={cn("w-full rounded-2xl border py-3 pl-12 pr-40 text-[16px] font-semibold text-slate-900 placeholder:font-medium placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20", selected ? "border-brand-300 bg-brand-50/30" : "border-hairline")}
                      />
                      {selected && (
                        <span className="pointer-events-none absolute right-10 top-1/2 -translate-y-1/2 text-[12.5px] font-semibold text-slate-500">
                          {[selected.est_minutes ? `${selected.est_minutes} min` : null, selected.fee_code ? (resolvedFee != null ? `$${resolvedFee} diagnostic` : "diagnostic fee") : "no fee"].filter(Boolean).join(" · ")}
                        </span>
                      )}
                      {typeQuery && (
                        <button type="button" onClick={() => { setTypeQuery(""); setTypeId(""); setComboOpen(true); }} title="Change the request" className="absolute right-3.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-600"><X className="h-4 w-4" /></button>
                      )}
                    </div>
                    {/* SVC-PICK-02: the whole catalog, grouped, in two balanced columns —
                        open while nothing is picked; typing filters it; ↓ ↑ Enter walk it. */}
                    {listOpen && (
                      <div ref={typeListRef} role="listbox" aria-label="Service requests" className="mt-2 max-h-[28rem] overflow-auto rounded-2xl border border-hairline bg-white p-2 shadow-e2">
                        {groups.length === 0 ? (
                          <div className="px-3 py-8 text-center text-[14px] text-slate-400">Nothing called “{typeQuery.trim()}” — try another word, or clear the box to see all {typeSearch.total}.</div>
                        ) : (
                          <div className="grid gap-x-3 sm:grid-cols-2">
                            {typeSearch.cols.map((col, ci) => (
                              <div key={ci} className="min-w-0">
                                {col.map(([cat, list]) => (
                                  <div key={cat} className="mb-2">
                                    <div className="flex items-center gap-2 px-2 pb-1 pt-2">
                                      <CatIcon cat={cat} />
                                      <span className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-slate-500">{categoryLabel(cat)}</span>
                                      <span className="text-[11px] tabular-nums text-slate-300">{list.length}</span>
                                    </div>
                                    {list.map((t) => {
                                      const idx = flatTypes.indexOf(t);
                                      const on = typeNav.active === idx;
                                      return (
                                        <button
                                          key={t.id} type="button" role="option" aria-selected={on} data-index={idx} tabIndex={-1}
                                          onClick={() => onSelectType(t)} onMouseEnter={() => typeNav.setActive(idx)}
                                          title={t.default_description ?? undefined}
                                          className={cn(
                                            "flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left transition-colors",
                                            on ? "bg-brand-50 ring-1 ring-inset ring-brand-200" : "hover:bg-slate-50",
                                          )}
                                        >
                                          <span className={cn("min-w-0 truncate text-[14px] font-semibold", on ? "text-brand-800" : "text-slate-800")}>{t.name}</span>
                                          <span className="shrink-0 text-[12px] tabular-nums text-slate-400">
                                            {t.est_minutes ? `${t.est_minutes} min` : ""}{t.fee_code ? "" : t.est_minutes ? " · no fee" : "no fee"}
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {(selected || booking) && (
                  <>
                    <div className="grid gap-5 md:grid-cols-2">
                      <div>
                        <Label>Urgency</Label>
                        {urgencySeg}
                      </div>
                      <div>
                        <Label>Fee &amp; PO</Label>
                        <div className="flex items-stretch gap-2">
                          {selected?.fee_code ? (
                            <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-hairline bg-white px-3 py-2">
                              {feeEditing ? (
                                <>
                                  <span className="text-slate-400">$</span>
                                  <input type="number" autoFocus value={feeOverride} onChange={(e) => setFeeOverride(e.target.value)} onBlur={() => setFeeEditing(false)}
                                    placeholder={resolvedFee != null ? String(resolvedFee) : ""} className="w-20 rounded-md border border-hairline px-2 py-0.5 text-[14px] font-semibold text-slate-900 focus:outline-none" />
                                  <span className="text-[12px] text-slate-400">override</span>
                                </>
                              ) : (
                                <button type="button" onClick={() => setFeeEditing(true)} title="Override the fee for this visit" className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-left">
                                  {isPlus && savings > 0 && standardFee && feeOverride.trim() === "" && <span className="text-[13px] font-medium text-slate-400 line-through">${standardFee.amount}</span>}
                                  <span className="text-[16px] font-extrabold text-slate-900">{effectiveFee != null ? `$${effectiveFee}` : "—"}{pay?.unit === "per_hour" ? "/hr" : ""}</span>
                                  {feeOverride.trim() !== "" ? (
                                    <span className="text-[12px] font-semibold text-slate-500">override</span>
                                  ) : isPlus && savings > 0 ? (
                                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700">Plan saves ${savings}</span>
                                  ) : (
                                    <span className="text-[12px] text-slate-400">{afterHours ? "after-hours rate" : isPlus ? "plan rate" : "standard rate"}</span>
                                  )}
                                  {afterHours && isPlus && savings > 0 && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700">after-hours</span>}
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="flex min-w-0 flex-1 items-center rounded-xl border border-hairline bg-slate-50 px-3 py-2 text-[13px] text-slate-500">{selected ? "No diagnostic fee" : "Fee set by the request"}</div>
                          )}
                          <input
                            value={po}
                            onChange={(e) => setPo(e.target.value)}
                            placeholder="PO number"
                            title="A vendor, builder or property-manager PO / job number — rides to the truck and prints on the invoice"
                            className="w-[150px] rounded-xl border border-hairline px-3 py-2 text-[13px] text-slate-800 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                          />
                        </div>
                      </div>
                    </div>

                    <label className="block">
                      <div className="mb-1.5 flex items-center gap-2">
                        <Label className="mb-0">Notes for the Tech</Label>
                        {/* CALL-TO-DISPATCH-01: say where the words came from. */}
                        {seededFromCall ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10.5px] font-semibold text-violet-700"><Sparkles className="h-3 w-3" /> from today's call</span>
                        ) : callLive ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700"><Phone className="h-3 w-3" /> on the phone now</span>
                        ) : null}
                      </div>
                      <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
                        className="w-full rounded-xl border border-hairline px-3.5 py-2.5 text-[14.5px] leading-relaxed focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
                    </label>

                    {filesRow}
                  </>
                )}
              </>
            )}

            {/* ================= STEP 2 — WHEN & WHO ================= */}
            {step === 2 && (
              <div className="space-y-3">
                {askPref && (
                  <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900">
                    <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-500" />
                    <span className="min-w-0">
                      The notes say this customer asks for <b>{askPref.names}</b>
                      <span className="text-amber-800/70"> — “{askPref.src}”</span>
                    </span>
                    <span className="ml-auto flex shrink-0 gap-1.5">
                      <button type="button" onClick={() => answerAsk(true)} disabled={resolveSignal.isPending}
                        className="rounded-md bg-brand-600 px-2.5 py-1 text-[12px] font-semibold text-white hover:bg-brand-700 disabled:opacity-60">
                        Yes — use {askPref.names}
                      </button>
                      <button type="button" onClick={() => answerAsk(false)} disabled={resolveSignal.isPending}
                        className="rounded-md border border-hairline bg-white px-2.5 py-1 text-[12px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60">
                        Not a preference
                      </button>
                    </span>
                  </div>
                )}

                {/* tech chips + urgency */}
                <div className="flex flex-wrap items-center gap-1.5">
                  <RowLabel>Tech</RowLabel>
                  <Chip on={techPick.length === 0} onClick={() => { techPickTouched.current = true; setTechPick([]); setPrefSaved(false); }}>Any</Chip>
                  {(techs ?? []).slice().sort((a, b) => {
                    const ap = prefs.preferredList.some((p) => p.empno === a.empno) ? 0 : 1;
                    const bp = prefs.preferredList.some((p) => p.empno === b.empno) ? 0 : 1;
                    return ap - bp || techFirst(a.empno).localeCompare(techFirst(b.empno));
                  }).map((t) => {
                    const star = prefs.preferredList.some((p) => p.empno === t.empno);
                    const ban = prefs.excluded.some((e) => e.empno === t.empno);
                    const on = techPick.includes(t.empno);
                    const unfit = !isFit(t.empno);
                    return (
                      <button key={t.empno} type="button" aria-pressed={on}
                        onClick={() => { techPickTouched.current = true; setPrefSaved(false); setTechPick((cur) => (cur.includes(t.empno) ? cur.filter((x) => x !== t.empno) : [...cur, t.empno])); }}
                        title={`${ban ? "The customer asked us not to send this tech. " : star ? "The customer's requested tech. " : ""}${unfit ? `${techFirst(t.empno)} ${whyNot(t.empno).join(" and ")} — not offered on the grid, still pickable. ` : ""}Pick more than one when any of them is fine.`}
                        className={cn("inline-flex h-[30px] items-center gap-1 rounded-full border px-3 text-[13px] font-semibold transition-colors",
                          on ? "border-brand-600 bg-brand-600 text-white" : ban ? "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100" : star ? "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100" : unfit ? "border-dashed border-slate-300 bg-transparent text-slate-400 hover:bg-slate-50" : "border-hairline bg-white text-slate-600 hover:bg-slate-50")}>
                        {star && <Star className="h-3 w-3 fill-current" />}{ban && <Ban className="h-3 w-3" />}{techFirst(t.empno)}
                      </button>
                    );
                  })}
                  {custno && pickDiffers && !prefSaved && (
                    <button type="button" onClick={() => void savePickAsPref()} disabled={prefSaving}
                      title="The customer asked for these techs — put it on the record so every booking opens on them"
                      className="ml-1 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[12px] font-semibold text-brand-700 hover:bg-brand-50 disabled:opacity-60">
                      <Star className="h-3 w-3" /> Save as this customer's {techPick.length === 1 ? "tech" : "techs"}
                    </button>
                  )}
                  {prefSaved && <span className="ml-1 inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-700"><Check className="h-3 w-3" /> Saved</span>}
                  <span className="ml-auto">{urgencySeg}</span>
                </div>
                {techPick.some((e) => !isFit(e)) && (
                  <p className="-mt-1 flex items-start gap-1.5 text-[12px] text-amber-800">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                    <span>{techPick.filter((e) => !isFit(e)).map((e) => `${techFirst(e)} ${whyNot(e).join(" and ")}`).join("; ")}.</span>
                  </p>
                )}

                {/* SKILL-STEER-01: what this call needs — level, specialties, the unit, a senior request. */}
                {(hasNeeds || equipAsk.length > 0 || custSpecAsk.length > 0 || needs.offerFirst.length > 0 || (prefs.senior && !prefs.senior.confirmed) || prefs.excluded.length > 0) && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <RowLabel>Needs</RowLabel>
                    {needs.minLevel && (
                      <span title={`Set by ${needs.levelWhy} — change it in Settings → Skills & Levels`}
                        className="inline-flex items-center gap-1 rounded-full border border-hairline bg-white px-2.5 py-1 text-[12px] font-semibold text-slate-700">
                        <GraduationCap className="h-3.5 w-3.5 text-slate-400" />{levelNeedText(levelLabels, needs.minLevel)}
                        {needs.levelWhy?.startsWith("the customer") && <span className="font-normal text-slate-400">· customer asked</span>}
                      </span>
                    )}
                    {needs.skills.map((x) => (
                      <span key={x.id} title={`Because of ${x.why}`}
                        className={cn("inline-flex items-center gap-1 rounded-full border border-hairline bg-white py-1 pl-2.5 text-[12px] font-semibold text-slate-700", x.equipKey ? "pr-1" : "pr-2.5")}>
                        {specName(x.id)}
                        <span className="font-normal text-slate-400">· {x.from === "equipment" ? "on file" : x.from === "customer" ? "customer" : "call type"}</span>
                        {x.equipKey && (
                          <button type="button" onClick={() => toggleEquip(x.equipKey!, false)} aria-label={`The call isn't about the ${specName(x.id)}`}
                            title="The call isn't about this unit — take it off"
                            className="ml-0.5 rounded-full p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </span>
                    ))}
                    {equipAsk.map((n) => (
                      <span key={n.key} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-slate-300 px-2.5 py-1 text-[12px] text-slate-500">
                        {n.auto ? <>{n.label} left off</> : <>Also has {/^[AEIOU]/i.test(n.label) ? "an" : "a"} {n.label} — about it?</>}
                        <button type="button" onClick={() => toggleEquip(n.key, true)} className="font-semibold text-brand-700 hover:text-brand-900">{n.auto ? "Undo" : "Yes"}</button>
                      </span>
                    ))}
                    {prefs.senior && !prefs.senior.confirmed && (
                      <span title={prefs.senior.src} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-slate-300 px-2.5 py-1 text-[12px] text-slate-500">
                        <GraduationCap className="h-3.5 w-3.5 text-slate-400" /> Notes say “{prefs.senior.label}” — a Senior Tech?
                        <button type="button" onClick={() => answerSenior(true)} disabled={resolveSignal.isPending} className="font-semibold text-brand-700 hover:text-brand-900 disabled:opacity-60">Yes</button>
                        <button type="button" onClick={() => answerSenior(false)} disabled={resolveSignal.isPending} className="font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-60">No</button>
                      </span>
                    )}
                    {custSpecAsk.map((c) => (
                      <span key={c.sigId} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-slate-300 px-2.5 py-1 text-[12px] text-slate-500">
                        Notes say “{c.label}” — needs {specName(c.specId)}?
                        <button type="button" onClick={() => resolveSignal.mutate({ id: c.sigId, status: "confirmed" })} disabled={resolveSignal.isPending} className="font-semibold text-brand-700 hover:text-brand-900 disabled:opacity-60">Yes</button>
                        <button type="button" onClick={() => resolveSignal.mutate({ id: c.sigId, status: "dismissed" })} disabled={resolveSignal.isPending} className="font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-60">No</button>
                      </span>
                    ))}
                    {prefs.excluded.map((e, i) => (
                      <span key={`x${i}`} title={e.src} className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[12px] font-semibold text-rose-700">
                        <Ban className="h-3 w-3" /> Not {e.name}{!e.confirmed && <span className="font-normal text-rose-500/70">· from notes</span>}
                      </span>
                    ))}
                    {hasNeeds && quals && (
                      <span className="text-[11.5px] text-slate-500">
                        · {qualified.length === 0 ? "nobody on the roster is marked for this — showing everyone" : `fits ${qualified.map(techFirst).join(", ")}`}
                      </span>
                    )}
                    {needs.offerFirst.length > 0 && (
                      <span className="text-[11.5px] text-slate-500">{hasNeeds ? "· " : ""}{needs.offerFirst.map(techFirst).join(" or ")} offered first</span>
                    )}
                  </div>
                )}

                {/* SCHED-AVAIL-01: the customer's days + a note; windows cross off on the grid's headers. */}
                <div className="flex flex-wrap items-center gap-1.5">
                  <RowLabel>Customer</RowLabel>
                  <Chip on={!isRestrictedDays(avail.days)} onClick={resetDays} title="No day restriction — Mon–Fri">Any day</Chip>
                  {[1, 2, 3, 4, 5, 6].map((d) => {
                    const on = avail.days.has(d);
                    const restricted = isRestrictedDays(avail.days);
                    const lit = on && (restricted || d === 6);
                    return (
                      <button key={d} type="button" onClick={() => toggleDow(d)} aria-pressed={lit}
                        title={d === 6 ? (on ? "Saturdays are on the grid — click to take them off" : "Customer can do Saturdays? Click to add them") : restricted ? (on ? `Customer can do ${DOW_LONG[d]} — click to remove` : `Add ${DOW_LONG[d]}`) : `Customer said ${DOW_LONG[d]}? Click — then add more days`}
                        className={cn("h-[30px] rounded-full border px-3 text-[13px] font-semibold transition-colors",
                          lit ? "border-brand-600 bg-brand-600 text-white" : on ? "border-hairline bg-white text-slate-600 hover:bg-slate-50" : "border-hairline bg-white text-slate-400 hover:bg-slate-50")}>
                        {DOW_LABEL[d]}
                      </button>
                    );
                  })}
                  <input
                    value={avail.note}
                    onChange={(e) => setAvail((a) => ({ ...a, note: e.target.value }))}
                    placeholder="Customer said…"
                    title="Works from home Tue/Thu, needs 30 min notice… — saved to the record with today's date when the visit is booked"
                    className="ml-auto h-[30px] min-w-[220px] flex-1 rounded-full border border-hairline px-3 text-[12.5px] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 sm:max-w-[380px]"
                  />
                </div>
                {(onFile.length > 0 || availText) && (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-slate-500">
                    {onFile.map(({ sig, parsed, when }) => (
                      <span key={sig.id} className="inline-flex items-center gap-1.5">
                        <Clock className="h-3 w-3 text-slate-400" />
                        <span>On file{when && !/—\s+said\s/.test(sig.value) ? ` (${new Date(`${when}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })})` : ""}: <span className="text-slate-700">“{sig.value}”</span>{sig.source !== "manual" ? <span className="text-slate-400"> · from notes</span> : null}</span>
                        {parsed && (
                          <button type="button" onClick={() => applyOnFile(parsed)} className="rounded-md border border-brand-200 bg-white px-1.5 py-px text-[11.5px] font-semibold text-brand-700 hover:bg-brand-50">Use</button>
                        )}
                      </span>
                    ))}
                    {availText && (onFileSame ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-slate-500">{resave ? "Saves again with today's date" : "Already on file — not saved again"}</span>
                        <button type="button" onClick={() => setResave((r) => !r)} className="rounded-md border border-hairline bg-white px-1.5 py-px text-[11.5px] font-semibold text-slate-600 hover:bg-slate-50">{resave ? "keep the old date" : "save again with today's date"}</button>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5">
                        <span className={cn(remember ? "text-slate-500" : "text-slate-400 line-through")}>Saves to the record: <span className="text-slate-700">{availText}</span> — {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                        <button type="button" onClick={() => setRemember((r) => !r)} className="rounded-md border border-hairline bg-white px-1.5 py-px text-[11.5px] font-semibold text-slate-600 hover:bg-slate-50">{remember ? "don't save" : "save it"}</button>
                      </span>
                    ))}
                  </div>
                )}

                {hasCoords ? (
                  <>
                    <div className={cn("relative overflow-hidden rounded-2xl border border-hairline bg-white", gridLoading && "opacity-70")}>
                      <AvailabilityGrid
                        days={gridDays}
                        windows={gridWindows}
                        cells={grid?.cells ?? null}
                        openDays={grid?.openDays ?? new Set()}
                        loading={gridLoading}
                        selected={empno && date ? { empno, dateISO: date, window: win } : null}
                        crossed={crossedAll}
                        crossedWindows={avail.windowsOff}
                        lightDays={lightDays}
                        today={todayISO}
                        onToggleDay={toggleCrossed}
                        onToggleWindow={toggleWindow}
                        onPick={pickOption}
                        onEarlier={() => setWeekOffset((o) => Math.max(0, o - 1))}
                        onLater={() => setWeekOffset((o) => o + 1)}
                        canEarlier={weekOffset > 0}
                        techFirst={techFirst}
                        windowLabel={windowLabel}
                        laterWindows={isInstall ? undefined : ["3-7"]}
                        laterOpen={laterOpen}
                        onToggleLater={() => setLaterOpen((v) => !v)}
                        afterHours={isInstall ? null : { byDay: oncallByDay, onPick: pickAfterHours }}
                      />
                      {gridLoading && <div className="pointer-events-none absolute inset-x-0 top-10 text-center text-[11px] font-medium text-slate-400">checking the boards…</div>}
                    </div>
                    {grid?.excludedNames.length ? <p className="text-[11.5px] text-slate-400">Not shown: {grid.excludedNames.join(", ")} — the customer asked us not to send them (the Tech row still can).</p> : null}

                    {/* The copilot's three best tech-days as one-line options; the picked one opens below. */}
                    {topOptions.length > 0 ? (
                      <div className="grid gap-2 sm:grid-cols-3">
                        {topOptions.map((o, i) => {
                          const on = !!selectedOpt && selectedOpt.empno === o.empno && selectedOpt.dateISO === o.dateISO && selectedOpt.window === o.window;
                          return (
                            <button key={`${o.empno}-${o.dateISO}-${o.window}`} type="button" onClick={() => pickOption(o)} aria-pressed={on}
                              className={cn("flex items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors", on ? "border-brand-500 bg-brand-50" : "border-hairline bg-white hover:border-slate-300")}>
                              <span className={cn("grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-extrabold", on ? "bg-brand-600 text-white" : i === 0 ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500")}>{i === 0 ? <Star className="h-3 w-3 fill-current" /> : i + 1}</span>
                              <span className="min-w-0">
                                <span className="block truncate text-[14px] font-extrabold text-slate-900">{techFirst(o.empno)} · {o.dayLabel}</span>
                                <span className="block truncate text-[12px] text-slate-500">{windowLabel(o.window)} · {ordinalOf(runOrderOf(o, on ? win : o.window, on && exactTime ? hhmmToHour(exactTime) : null).pos + 1)} of {o.stops.length + 1} · {o.addedMiles < 0.5 ? "no extra miles" : `+${Math.round(o.addedMiles)} mi`}</span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    ) : grid && !gridLoading ? (
                      <p className="text-[12px] text-slate-400">Nothing open in view — try later ›, another tech, or a crossed-off day.</p>
                    ) : null}
                  </>
                ) : (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800">No map location on file for this address, so the grid can't score the boards — set the tech, day and window by hand below.</div>
                )}

                {/* the chosen day, top to bottom — drag the new visit into its place */}
                {selectedOpt && (
                  <TechDayList
                    suggestion={selectedOpt}
                    chosenWindow={win}
                    onWindowChange={onPickWindow}
                    exactHour={exactTime ? hhmmToHour(exactTime) : null}
                    onCommitTime={onCommitTime}
                    windows={isInstall ? INSTALL_WINDOWS : WINDOWS}
                    windowLabel={windowLabel}
                    preview={{ name: customerName, type: requestLabel, plus: isPlus, asap: urgency === "asap", city: city ?? null, minutes: selected?.est_minutes ?? null }}
                    extra={win === "AFTER" && empno && date ? <><b className="text-slate-800">{techName(empno)}</b> is on call — booked as <b className="text-slate-800">After Hours</b>, the after-hours fee applies{isPlus ? " (members keep their rate)" : ""}.</> : null}
                  />
                )}
                {!selectedOpt && win === "AFTER" && empno && date && (
                  <div className="rounded-xl border border-brand-100 bg-white px-3 py-2 text-[12.5px] text-slate-600">
                    <b className="text-slate-800">{techName(empno)}</b> is on call {prettyDate} — booked as <b className="text-slate-800">After Hours</b>, the after-hours fee applies{isPlus ? " (members keep their rate)" : ""}.
                  </div>
                )}

                {/* by hand */}
                <div className="rounded-xl border border-hairline bg-slate-50/60 px-3 py-2">
                  <button type="button" onClick={() => setByHand((v) => !v)} className="text-[12.5px] font-semibold text-brand-700 hover:text-brand-900">
                    {byHand || !hasCoords ? "Tech · Day · Window by hand" : "Set by hand…"}
                  </button>
                  {(byHand || !hasCoords) && (
                    <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <label className="block text-sm">
                        <span className="mb-1 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-500">
                          <span>Tech</span>
                          {empno && <button type="button" onClick={(e) => { e.stopPropagation(); setEmpno(""); }} className="rounded normal-case text-brand-600 transition-colors hover:text-brand-800">Clear</button>}
                        </span>
                        <select value={empno} onChange={(e) => { setEmpno(e.target.value); syncByHand(e.target.value, date, win); }} className="w-full rounded-lg border border-hairline px-3 py-2.5 text-[15px]">
                          <option value="">Any tech</option>
                          {(techs ?? []).slice().sort((a, b) => {
                            const ap = prefs.preferredList.some((p) => p.empno === a.empno && p.confirmed) ? 0 : 1;
                            const bp = prefs.preferredList.some((p) => p.empno === b.empno && p.confirmed) ? 0 : 1;
                            return ap - bp || techName(a.empno).localeCompare(techName(b.empno));
                          }).map((t) => {
                            const star = prefs.preferredList.some((p) => p.empno === t.empno && p.confirmed) ? "★ " : "";
                            const ban = prefs.excluded.some((e) => e.empno === t.empno && e.confirmed) ? "⚠ " : "";
                            return <option key={t.empno} value={t.empno}>{star}{ban}{techName(t.empno)}{isFit(t.empno) ? "" : " — not a fit"}</option>;
                          })}
                        </select>
                      </label>
                      <label className="block text-sm">
                        <span className="mb-1 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-500">
                          <span>Day</span>
                          {date && <button type="button" onClick={(e) => { e.stopPropagation(); setDate(""); setExactTime(null); }} className="rounded normal-case text-brand-600 transition-colors hover:text-brand-800">Clear</button>}
                        </span>
                        <input type="date" value={date} onChange={(e) => { setDate(e.target.value); setExactTime(null); syncByHand(empno, e.target.value, win); }} className="w-full rounded-lg border border-hairline px-3 py-2.5 text-[15px]" />
                      </label>
                      <label className="block text-sm">
                        <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Arrival window{isInstall && <span className="ml-1 normal-case text-indigo-500">· install</span>}</span>
                        {isInstall ? (
                          <>
                            <select
                              value={INSTALL_WINDOWS.includes(win) ? win : "__custom__"}
                              onChange={(e) => { if (e.target.value === "__custom__") { winTouched.current = true; setWin(""); } else onPickWindow(e.target.value); }}
                              className="w-full rounded-lg border border-hairline px-3 py-2.5 text-[15px]"
                            >
                              {INSTALL_WINDOWS.map((w) => <option key={w} value={w}>{INSTALL_WINDOW_LABELS[w] ?? w}</option>)}
                              <option value="__custom__">Custom…</option>
                            </select>
                            {!INSTALL_WINDOWS.includes(win) && (
                              <input
                                type="text"
                                value={win}
                                autoFocus
                                onChange={(e) => onPickWindow(e.target.value)}
                                placeholder="e.g. 7:30–9 or 1–5"
                                className="mt-1.5 w-full rounded-lg border border-hairline px-3 py-2.5 text-[15px]"
                              />
                            )}
                          </>
                        ) : (
                          <select value={win} onChange={(e) => onPickWindow(e.target.value)} className="w-full rounded-lg border border-hairline px-3 py-2.5 text-[15px]">
                            {WINDOWS.map((w) => <option key={w} value={w}>{w === "8AM" ? "8AM (first call)" : w === "AFTER" ? "After Hours (on call)" : w}</option>)}
                            {/* PAIR-01 (Seth 2026-09-30): ESC's special codes, where ESC dispatchers expect them. */}
                            <optgroup label="Special codes (color + lock, like ESC)">
                              <option value="PAIR">PAIR — keep together, don&rsquo;t split the calls</option>
                              <option value="TECH">TECH — tech-specific</option>
                              <option value="LONG">LONG — long call</option>
                              <option value="QUOTE">QUOTE — sales visit</option>
                              <option value="PART">PART — part call</option>
                            </optgroup>
                          </select>
                        )}
                      </label>
                    </div>
                  )}
                </div>
                {(selExcluded || selMiss.length > 0 || seniorMiss) && (
                  <div className="space-y-1.5">
                    {selExcluded && (
                      <div className="flex items-start gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[11.5px] text-rose-700">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span><strong>{techName(empno)}</strong> is the tech this customer asked us <strong>not</strong> to send{!selExcluded.confirmed && " (from notes)"}. <span className="text-rose-500">“{selExcluded.src}”</span></span>
                      </div>
                    )}
                    {selMiss.length > 0 && (
                      <div className="flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11.5px] text-amber-800">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span><strong>{techName(empno)}</strong> {selMiss.join(" and ")}.</span>
                      </div>
                    )}
                    {seniorMiss && (
                      <div className="flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11.5px] text-amber-800">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>The notes say this customer wants a <strong>senior tech</strong> (“{prefs.senior?.label}”, not confirmed). <strong>{techName(empno)}</strong> {quals?.get(empno)?.level ? `is a ${levelLabels[(quals.get(empno)!.level as number) - 1]}` : "has no level set"}.</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ================= STEP 3 — CONFIRM ================= */}
            {step === 3 && (
              <div className="space-y-5">
                {empno && date && (
                  <div className="grid grid-cols-[56px_minmax(0,1fr)_auto] items-center gap-4 rounded-2xl border border-brand-200 bg-gradient-to-br from-brand-50 to-white px-4 py-4 shadow-sm">
                    <span className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-brand-600 to-brand-900 text-[18px] font-extrabold text-white">{initialsOf(techName(empno))}</span>
                    <div className="min-w-0">
                      <div className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-brand-600">Ready to schedule</div>
                      <div className="mt-0.5 truncate text-[22px] font-extrabold leading-tight tracking-tight text-slate-900">
                        {techName(empno)}<span className="px-1.5 font-medium text-slate-300">·</span>{prettyDate}<span className="px-1.5 font-medium text-slate-300">·</span>{win}
                        {exactTime && <span className="ml-1.5 text-[15px] font-bold text-slate-500">at {hourToHHMM(hhmmToHour(exactTime)).replace(/^0/, "")}</span>}
                      </div>
                      <div className="mt-1 truncate text-[12.5px] text-slate-600">
                        {selectedOpt && (() => {
                          const ro = runOrderOf(selectedOpt, win, exactTime ? hhmmToHour(exactTime) : null);
                          const prev = ro.after ? `${ro.after.name ?? ro.after.city ?? "a stop"}${ro.after.city && ro.after.name ? ` in ${ro.after.city}` : ""}` : null;
                          return <>{ordinalOf(ro.pos + 1)} call of {selectedOpt.stops.length + 1}{prev ? ` · after ${prev}` : " · first of the day"} · {selectedOpt.addedMiles < 0.5 ? "no extra miles" : `+${Math.round(selectedOpt.addedMiles)} mi`}</>;
                        })()}
                        {selectedOpt && toldLines.length > 0 && " · "}
                        {toldLines.map((l, i) => <span key={l}>{i > 0 && " · "}<b className="font-semibold text-slate-800">{l}</b></span>)}
                      </div>
                    </div>
                    <Button onClick={scheduleNow} disabled={!canBook} className="px-5 py-3 text-[15px]">
                      <CalendarPlus className="h-4 w-4" /> {busy ? "Booking…" : "Schedule"}
                    </Button>
                  </div>
                )}

                <WhoToReach people={people} state={reach} onChange={setReach} canSave={canSaveContacts} />

                <label className="block">
                  <Label>Notes for the Tech</Label>
                  <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
                    className="w-full rounded-2xl border border-hairline px-4 py-3 text-[14.5px] leading-relaxed focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
                </label>

                {filesRow}

                {actionError && (
                  <p className="rounded-md bg-rose-50 px-2.5 py-1.5 text-xs font-medium text-rose-700">{actionError}</p>
                )}
                {dnsBlocked && (
                  <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm font-medium text-rose-700">
                    This account is marked <b>Do Not Service</b>
                    {dnsFlag?.dns_reason ? <> — {dnsFlag.dns_reason}</> : dnsCust ? <> (ESC credit rating: {dnsCust})</> : null}.
                    Booking is disabled. An admin can clear the mark from the customer header.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* ---- THE TICKET: the board card as it will land, then the facts ---- */}
          <aside className="h-fit rounded-2xl border border-hairline bg-slate-50/80 p-3.5 lg:sticky lg:top-0">
            <Label>The Ticket</Label>
            <TicketPreview
              window={win}
              name={customerName}
              type={requestLabel}
              address={address ?? null}
              city={city ?? null}
              plus={isPlus}
              asap={urgency === "asap"}
              headsUp={reached.headsUp ? `${reached.headsUp.method === "text" ? "Text" : "Call"} ${reached.headsUp.name ? `${reached.headsUp.name.split(/\s+/)[0]} ` : ""}${prettyPhone(reached.headsUp.phone)} on the way` : null}
            />
            <div className="mt-3.5 space-y-3 text-[13px]">
              <RailLine label="Request" value={selected ? [selected.name, urgencyText, feeText, isPlus && savings > 0 && feeOverride.trim() === "" ? `plan saves $${savings}` : "", po ? `PO ${po}` : ""].filter(Boolean).join(" · ") : booking ? existingTicket!.summary : null} empty="step 1" />
              <RailLine label="Watch For" value={[...prefs.preferredList.map((p) => `requests ${p.name}${p.confirmed ? "" : " (from notes)"}`), ...prefs.excluded.map((e) => `not ${e.name}`), needText ? `needs ${needText}` : "", prefs.senior && !prefs.senior.confirmed ? "senior tech (from notes)" : "", ...consChips].filter(Boolean).join(" · ") || null} empty="nothing flagged" />
              <RailLine label="When & Who" value={whenText} empty="step 2" />
              <RailLine label="Who to Reach" value={toldLines.join(" · ") || null} empty="step 3" />
              <RailLine label="Files" value={filesText} empty="none" />
            </div>
          </aside>
        </div>
      </div>
    </Modal>
  );
}

/* ----------------------------- small parts ----------------------------- */

/** One icon tile per request category — the catalog reads as a menu board. */
const CAT_STYLE: Record<string, { Icon: typeof Wrench; cls: string }> = {
  service: { Icon: Wrench, cls: "bg-sky-50 text-sky-600" },
  maintenance: { Icon: ShieldCheck, cls: "bg-emerald-50 text-emerald-600" },
  sales: { Icon: BadgeDollarSign, cls: "bg-amber-50 text-amber-600" },
  install: { Icon: Hammer, cls: "bg-violet-50 text-violet-600" },
  followup: { Icon: RotateCcw, cls: "bg-slate-100 text-slate-500" },
  plumbing: { Icon: Droplets, cls: "bg-blue-50 text-blue-600" },
};
function CatIcon({ cat, className }: { cat: string; className?: string }) {
  const st = CAT_STYLE[cat] ?? { Icon: Wrench, cls: "bg-slate-100 text-slate-500" };
  return <span className={cn("grid h-6 w-6 shrink-0 place-items-center rounded-md", st.cls, className)}><st.Icon className="h-3.5 w-3.5" /></span>;
}

const initialsOf = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "?";
const ordinalOf = (n: number) => `${n}${n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th"}`;

function Label({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("mb-1.5 text-[10.5px] font-extrabold uppercase tracking-[0.1em] text-slate-500", className)}>{children}</div>;
}
function RowLabel({ children }: { children: ReactNode }) {
  return <span className="mr-1 text-[10.5px] font-extrabold uppercase tracking-[0.1em] text-slate-400">{children}</span>;
}
function Chip({ on, onClick, children, title }: { on: boolean; onClick: () => void; children: ReactNode; title?: string }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on} title={title}
      className={cn("h-[30px] rounded-full border px-3 text-[13px] font-semibold transition-colors", on ? "border-brand-600 bg-brand-600 text-white" : "border-hairline bg-white text-slate-600 hover:bg-slate-50")}>
      {children}
    </button>
  );
}

function RailLine({ label, value, empty }: { label: string; value: string | null; empty?: string }) {
  return (
    <div>
      <div className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-slate-400">{label}</div>
      {value ? <div className="mt-0.5 font-semibold leading-snug text-slate-800">{value}</div> : <div className="mt-0.5 text-slate-400">{empty ?? "—"}</div>}
    </div>
  );
}

/** The board card exactly as it will land (PegasusBoardBlock's compact form). */
function TicketPreview({ window: win, name, type, address, city, plus, asap, headsUp }: {
  window: string; name: string; type: string | null; address: string | null; city: string | null; plus: boolean; asap: boolean; headsUp: string | null;
}) {
  const showWin = win && win !== "Anytime";
  const c = windowColors(showWin ? win : null);
  return (
    <div className={cn("relative overflow-hidden rounded-lg border border-l-4 border-black/[0.08] bg-white px-3 py-2.5 shadow-sm", c.border)}>
      <span aria-hidden className={cn("pointer-events-none absolute inset-0 opacity-[0.22]", c.bg)} />
      <span className="relative block">
        <div className="mb-1 flex min-w-0 flex-wrap items-center gap-1.5">
          {showWin ? (
            <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider", c.bg, c.text)}>{win}</span>
          ) : (
            <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600">Window</span>
          )}
          {/* No "Pegasus" chip (Seth 2026-10-21): once live every booking is Pegasus — the exception worth a pill is an ESC call. */}
          {plus && <span className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800 ring-1 ring-amber-300/70">Plus</span>}
          {asap && <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">ASAP</span>}
        </div>
        <div className="text-[13.5px] font-bold leading-snug text-slate-900">{name}</div>
        <div className="text-xs leading-snug text-slate-500">{type ?? <span className="text-slate-400">request · step 1</span>}</div>
        {(address || city) && <div className="mt-0.5 text-[11.5px] leading-snug text-slate-500">{[address, city].filter(Boolean).join(" · ")}</div>}
        {headsUp && <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-500"><Phone className="h-3 w-3" /> {headsUp}</div>}
      </span>
    </div>
  );
}

/** Files & photos as one row: + Add · each staged file as a chip · the customer's request photos as a toggle chip. */
function FilesRow({ staged, onAdd, onRemove, requestPhotoCount, requestWhen, attachRequest, onToggleAttach, children }: {
  staged: File[]; onAdd: () => void; onRemove: (i: number) => void;
  requestPhotoCount: number; requestWhen: string | null; attachRequest: boolean; onToggleAttach: () => void;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <RowLabel>Files &amp; Photos</RowLabel>
      <button type="button" onClick={onAdd} className="inline-flex h-[30px] items-center gap-1 rounded-full border border-hairline bg-slate-50 px-3 text-[12.5px] font-semibold text-slate-600 hover:border-brand-300 hover:text-brand-700">
        <Plus className="h-3.5 w-3.5" /> Add
      </button>
      {children}
      {requestPhotoCount > 0 && (
        <button type="button" onClick={onToggleAttach} aria-pressed={attachRequest}
          title={attachRequest ? "Riding onto this call, marked “from the customer” — click to leave them off" : "Left off this call — click to attach them"}
          className={cn("inline-flex h-[30px] items-center gap-1.5 rounded-full border px-3 text-[12.5px] font-semibold transition-colors", attachRequest ? "border-brand-200 bg-brand-50 text-brand-800" : "border-hairline bg-white text-slate-400 line-through")}>
          <Paperclip className="h-3.5 w-3.5" /> {requestPhotoCount} customer photo{requestPhotoCount === 1 ? "" : "s"}{requestWhen ? ` · ${fileWhen(requestWhen)}` : ""}
        </button>
      )}
      {staged.map((f, i) => (
        <span key={`${f.name}-${i}`} className="inline-flex h-[30px] items-center gap-1.5 rounded-full border border-hairline bg-white pl-3 pr-1.5 text-[12.5px] font-medium text-slate-700">
          <FileText className="h-3.5 w-3.5 text-slate-400" />
          <span className="max-w-[220px] truncate">{f.name}</span>
          <span className="tabular-nums text-slate-400">{prettySize(f.size)}</span>
          <button type="button" onClick={() => onRemove(i)} title="Remove" className="rounded-full p-0.5 text-slate-300 hover:bg-slate-100 hover:text-red-600"><X className="h-3 w-3" /></button>
        </span>
      ))}
      {staged.length === 0 && requestPhotoCount === 0 && <span className="text-[12px] text-slate-400">none</span>}
    </div>
  );
}
